import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShoppingCart, Search, Plus, Minus, Trash2, UserPlus, PackagePlus, X,
  Banknote, CreditCard, QrCode, User, Package, IndianRupee, History, Camera, Truck, Keyboard,
  FileText, Upload, CheckCircle, AlertCircle, HelpCircle,
} from 'lucide-react';
import { posAPI, purchasesAPI, khataAPI, customersAPI, catalogAPI, supplierAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useOffline } from '@/contexts/OfflineContext';
import CameraScanner from '@/components/CameraScanner';
import SubscriptionWarningBanner from '@/components/SubscriptionWarningBanner';
import { useIsMobile } from '@/hooks/useIsMobile';
import ReceiptModal from '@/components/ReceiptModal';
import {
  getAllProducts,
  getAllCustomers,
  putProducts,
  removeProduct,
  replaceAllProducts,
  putCustomers,
  enqueueOfflineSale,
  generateTempBillNumber,
  getProductsStoreId,
  setProductsStoreId,
} from '@/lib/offlineDB';
import { isProductsDirty, markProductsClean, markProductsDirty, isProductSyncStale, markProductsSynced } from '@/lib/offlineSync';
import { supabase } from '@/lib/supabaseClient';
import { successHaptic, errorHaptic } from '@/lib/haptics';

// ── GST helpers ───────────────────────────────────────────────────────────────

const calcGst = (item_total, gst_rate) =>
  gst_rate > 0 ? Math.round(item_total * gst_rate / (100 + gst_rate) * 100) / 100 : 0;

const fmt  = (n) => '₹' + Number(n).toFixed(2);
const fmtN = (n) => '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

// Reconcile a single cart item against a fresh products list.
// Updates mrp (and derived unit_price / totals) only when the
// catalog price changed. Leaves items untouched if the price is the same.
const reconcileCartItem = (cartItem, freshProds) => {
  const fresh = freshProds.find(p => p.catalog_id === cartItem.catalog_id);
  if (!fresh) return cartItem;
  const nameChanged = fresh.name != null && fresh.name !== cartItem.product_name;
  const mrpChanged  = fresh.mrp  != null && fresh.mrp  !== cartItem.mrp;
  if (!nameChanged && !mrpChanged) return cartItem;
  let updated = nameChanged ? { ...cartItem, product_name: fresh.name } : cartItem;
  if (!mrpChanged) return updated;
  const unit_price = calcUnitCost(fresh.mrp, updated.discount, updated.discount_type);
  const item_total = Math.round(updated.qty * unit_price * 100) / 100;
  return { ...updated, mrp: fresh.mrp, unit_price, item_total, gst_amount: calcGst(item_total, updated.gst_rate) };
};

// ── Discount helpers ──────────────────────────────────────────────────────────

const calcDiscountAmount = (mrp, discount, discount_type) => {
  const d = parseFloat(discount) || 0;
  if (discount_type === 'percent') {
    return Math.round(mrp * Math.min(d, 100) / 100 * 100) / 100;
  }
  return Math.min(d, mrp);
};

const calcUnitCost = (mrp, discount, discount_type) => {
  const discAmt = calcDiscountAmount(mrp, discount, discount_type);
  return Math.max(0, Math.round((mrp - discAmt) * 100) / 100);
};

// EAN-13/UPC-A check-digit validation. Advisory only — this store's own catalog also uses
// short custom codes (e.g. barcode "179"), which are never standard EAN/UPC and correctly
// won't match this check, so it must never block Quick Add, only warn. UPC-A (12 digits) is
// validated by zero-padding to EAN-13, its defined equivalent. EAN-8 is intentionally not
// covered (different, unverified weighting) — those codes just skip this check silently,
// same as any non-12/13-digit code.
function looksLikeCorruptedBarcode(code) {
  if (!/^\d+$/.test(code) || (code.length !== 12 && code.length !== 13)) return false;
  const padded = code.length === 12 ? '0' + code : code;
  const digits = padded.slice(0, 12).split('').map(Number);
  const sum = digits.reduce((s, d, i) => s + d * (i % 2 === 0 ? 1 : 3), 0);
  const computed = (10 - (sum % 10)) % 10;
  return computed !== Number(padded[12]);
}

// ── Payment mode config ───────────────────────────────────────────────────────

const PAYMENT_MODES = [
  { key: 'cash',  label: 'Cash',         Icon: Banknote,   color: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
  { key: 'upi',   label: 'UPI',          Icon: QrCode,     color: 'bg-purple-600 hover:bg-purple-700 text-white' },
  { key: 'card',  label: 'Card',         Icon: CreditCard, color: 'bg-blue-600 hover:bg-blue-700 text-white' },
  { key: 'khata', label: 'Khata/Credit', Icon: User,       color: 'bg-gray-500 hover:bg-gray-600 text-white' },
];

// ── Offline receipt helper ────────────────────────────────────────────────────

function buildFakeReceiptSale(temp_bill_number, payload, cart, cartTotals, extras = {}) {
  return {
    bill_number:          temp_bill_number,
    created_at:           new Date().toISOString(),
    items: cart.map(item => ({
      catalog_id:    item.catalog_id,
      product_name:  item.product_name,
      quantity:      item.qty,
      unit_price:    item.unit_price,
      mrp: item.mrp,
      gst_rate:      item.gst_rate,
      gst_amount:    item.gst_amount,
      item_total:    item.item_total,
      discount:      item.discount ?? 0,
      discount_type: item.discount_type ?? 'percent',
    })),
    subtotal:              cartTotals.subtotal,
    overall_discount:      payload.overall_discount,
    overall_discount_type: payload.overall_discount_type,
    round_off:             payload.round_off,
    total_amount:          cartTotals.rounded_total,
    total_gst:             cartTotals.total_gst,
    gst_inclusive:         true,
    payment_mode:          payload.payment_mode,
    split_payments:        payload.split_payments,
    customer_name:         payload.customer_name,
    customer_mobile:       payload.customer_mobile,
    khata_cleared:         payload.khata_cleared || 0,
    khata_remaining:       payload.khata_remaining || 0,
    existing_khata_balance: extras.existing_khata_balance ?? 0,
    khata_added:           extras.khata_added ?? 0,
    status:                'active',
  };
}

// ── Slot helpers ──────────────────────────────────────────────────────────────

const EMPTY_SLOT = { cart: [], tempProducts: [], customer: null, overallDiscount: '', overallDiscountType: 'percent' };
const makeEmptySlots = () => [{ ...EMPTY_SLOT }, { ...EMPTY_SLOT }, { ...EMPTY_SLOT }];

// Blocks e/E/+/- which browsers allow in type="number" inputs by default.
const blockNonNumeric = e => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); };

// Qty input with local string state so intermediate keystrokes (e.g. "0" while
// typing "0.5") don't trigger premature validation / item removal.
function QtyInput({ value, onCommit, onRemove, inputRef, onKeyDown, onFocus, onEscape, className = 'w-12' }) {
  const [local, setLocal] = React.useState(String(value));
  const skipBlurCommitRef = React.useRef(false);

  useEffect(() => { setLocal(String(value)); }, [value]);

  const commit = () => {
    const parsed = parseFloat(local);
    const qty = Math.round((isNaN(parsed) ? 0 : Math.abs(parsed)) * 100) / 100;
    if (qty < 0.01) { onRemove(); return; }
    onCommit(qty);
  };

  return (
    <input
      ref={inputRef}
      type="number"
      value={local}
      min="0.01"
      step="any"
      onChange={e => setLocal(e.target.value)}
      onWheel={e => e.currentTarget.blur()}
      onBlur={() => {
        if (skipBlurCommitRef.current) { skipBlurCommitRef.current = false; return; }
        commit();
      }}
      onFocus={e => { e.target.select(); onFocus?.(e); }}
      onKeyDown={e => {
        blockNonNumeric(e);
        if (e.key === 'Escape') {
          e.preventDefault();
          skipBlurCommitRef.current = true;
          setLocal(String(value));
          onEscape?.();
          return;
        }
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          const n = Math.round(Math.abs(parseFloat(local) || 0) * 100) / 100;
          let next;
          if (e.key === 'ArrowUp') {
            next = Number.isInteger(n) ? n + 1 : Math.ceil(n);
          } else {
            next = Number.isInteger(n) ? Math.max(0, n - 1) : Math.floor(n);
          }
          if (next < 0.01) { onRemove(); return; }
          setLocal(String(next));
          onCommit(next);
          const el = e.target;
          requestAnimationFrame(() => el.select());
          return;
        }
        if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) { e.preventDefault(); commit(); }
        onKeyDown(e);
      }}
      className={`${className} text-center border border-border rounded px-1 py-1 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
    />
  );
}

// Local-state buffer for price inputs — prevents parseFloat from stripping
// trailing decimals mid-type. Commits to cart only on blur or Enter.
const PriceInput = React.forwardRef(function PriceInput(
  { value, onCommit, onBlurExtra, onKeyDown, onEscape, className, minValue, ...rest }, ref
) {
  const [local, setLocal] = React.useState(value != null ? String(value) : '');
  const skipBlurCommitRef = React.useRef(false);
  React.useEffect(() => { setLocal(value != null ? String(value) : ''); }, [value]);
  const commit = (raw) => {
    const n = parseFloat(raw);
    const val = isNaN(n) ? 0 : Math.max(0, n);
    if (minValue !== undefined && val < minValue) {
      setLocal(value != null ? String(value) : '');
      return;
    }
    onCommit(val);
  };
  return (
    <input
      ref={ref}
      type="number"
      value={local}
      onChange={e => setLocal(e.target.value)}
      onBlur={async (e) => {
        if (skipBlurCommitRef.current) { skipBlurCommitRef.current = false; return; }
        commit(e.target.value);
        if (onBlurExtra) await onBlurExtra(e);
      }}
      onKeyDown={e => {
        blockNonNumeric(e);
        if (e.key === 'Escape') {
          e.preventDefault();
          skipBlurCommitRef.current = true;
          setLocal(value != null ? String(value) : '');
          onEscape?.();
          return;
        }
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          const n = parseFloat(local) || 0;
          const next = e.key === 'ArrowUp'
            ? (Number.isInteger(n) ? n + 1 : Math.ceil(n))
            : (Number.isInteger(n) ? Math.max(0, n - 1) : Math.floor(n));
          if (minValue !== undefined && next < minValue) return;
          setLocal(String(next));
          onCommit(next);
          const el = e.target;
          requestAnimationFrame(() => el.select());
          return;
        }
        if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) commit(e.target.value);
        if (onKeyDown) onKeyDown(e);
      }}
      className={className}
      {...rest}
    />
  );
});

// ── Main component ────────────────────────────────────────────────────────────

export default function PointOfSale() {
  const location  = useLocation();
  const navigate  = useNavigate();
  const { user, storeProfile } = useAuth();
  const { isOnline, refreshPendingCount, lastSyncedAt } = useOffline();

  // Products & customers
  const [products, setProducts]           = useState([]);
  const [customers, setCustomers]         = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [usingCachedData, setUsingCachedData] = useState(false);
  const [catalogRefreshing, setCatalogRefreshing] = useState(false);
  const [posRefreshKey, setPosRefreshKey] = useState(0);

  // Search
  const [searchQuery, setSearchQuery]     = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedIdx, setSelectedIdx]     = useState(-1);
  const [showDropdown, setShowDropdown]   = useState(false);
  const [cameraOpen, setCameraOpen]       = useState(false);
  const [lastScannedName, setLastScannedName] = useState('');  // shown in camera success overlay
  const preWarmStreamRef                  = useRef(null);      // MediaStream pre-obtained on mount
  const isMobile                          = useIsMobile();
  const isMobileRef                       = useRef(isMobile);
  useEffect(() => { isMobileRef.current = isMobile; }, [isMobile]);

  useEffect(() => { if (isMobile) setCameraOpen(true); }, [isMobile]);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  // Desktop-only: the barcode scanner requires document.activeElement === searchRef.current.
  const refocusSearch = () => { if (!isMobile) searchRef.current?.focus(); };

  // Cart
  const [cart, setCart] = useState([]);
  const qtyInputRefs          = useRef([]);   // ref array indexed by cart row
  const spInputRefs           = useRef([]);   // MRP inputs, indexed by cart row
  const discountInputRefs     = useRef([]);   // discount inputs, indexed by cart row
  const upInputRefs           = useRef([]);   // unit price inputs, indexed by cart row
  const trashInputRefs        = useRef([]);   // trash/remove buttons, indexed by cart row
  const lastAddedCatalogIdRef  = useRef(null); // catalog_id of most recently added/updated product
  const pendingFocusIdxRef = useRef(null); // unused — kept to avoid removing ref silently
  const mrpSaveModeIdxRef  = useRef(null); // set when Alt+M activates; triggers stock-master save on blur
  const mrpOriginalValueRef = useRef(null); // MRP captured at the moment Alt+M is pressed
  const [mrpSaveModeIdx, setMrpSaveModeIdx] = useState(null); // drives amber ring re-render
  const discountOriginalRef = useRef(null); // discount value captured on focus; restored on Escape

  // Customer
  const [customer, setCustomer]               = useState(null);
  const [customerSearch, setCustomerSearch]   = useState('');
  const [showCustomerDrop, setShowCustomerDrop] = useState(false);
  const [customerSelectedIdx, setCustomerSelectedIdx] = useState(-1);
  const customerInputRef = useRef(null);
  const customerDropdownRef = useRef(null);
  // Supplier-only: true when checkout was blocked because the selected retailer is not connected
  const [retailerGateError, setRetailerGateError] = useState(null);

  // Quick-add customer modal
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickName, setQuickName]       = useState('');
  const [quickMobile, setQuickMobile]   = useState('');
  const [quickLoading, setQuickLoading] = useState(false);

  // Quick-add product modal (triggered when scanned barcode not in system)
  const [qaProductOpen,      setQaProductOpen]      = useState(false);
  const [qaProductBarcode,   setQaProductBarcode]   = useState('');
  const [qaProductForm,      setQaProductForm]      = useState({ name: '', mrp: '', cost_price: '', qty: '1', discount: '' });
  const [qaProductLoading,   setQaProductLoading]   = useState(false);
  const [qaDiscountMode,     setQaDiscountMode]     = useState('percent'); // 'percent' | 'rupees'
  const [qaDiscountRs,       setQaDiscountRs]       = useState('');
  const [qaSuggestingBarcode, setQaSuggestingBarcode] = useState(false);
  const [qaSuggestCount,      setQaSuggestCount]      = useState(0);

  // Document order import modal
  const [orderImportOpen,     setOrderImportOpen]     = useState(false);
  const [orderImportStep,     setOrderImportStep]     = useState(1);      // 1=upload, 2=review
  const [orderImportMode,     setOrderImportMode]     = useState('file'); // 'file' | 'text'
  const [orderImportFile,     setOrderImportFile]     = useState(null);
  const [orderImportText,     setOrderImportText]     = useState('');
  const [orderImportLoading,  setOrderImportLoading]  = useState(false);
  const [orderImportDragging, setOrderImportDragging] = useState(false);
  const [parsedOrder,         setParsedOrder]         = useState(null);
  const [matchedOrder,        setMatchedOrder]        = useState([]);

  // Unified Quick Add opener — only alphabets/spaces → pre-fill name; digits/alphanumeric/symbols → pre-fill barcode
  const openQaModal = (input = '') => {
    const trimmed = input.trim();
    const onlyLetters = trimmed.length > 0 && /^[a-zA-Z\s]+$/.test(trimmed);
    if (onlyLetters) {
      setQaProductBarcode('');
      setQaProductForm({ name: trimmed, mrp: '', cost_price: '', qty: '1' });
    } else if (trimmed.length > 0) {
      setQaProductBarcode(trimmed);
      setQaProductForm({ name: '', mrp: '', cost_price: '', qty: '1', discount: '' });
    } else {
      setQaProductBarcode('');
      setQaProductForm({ name: '', mrp: '', cost_price: '', qty: '1', discount: '' });
    }
    setQaDiscountMode('percent');
    setQaDiscountRs('');
    setQaProductOpen(true);
    setQaSuggestCount(0);
    setShowDropdown(false);
  };

  // Dismiss Quick Add Product without saving — also clears the search bar so billing can continue
  const closeQaModal = () => {
    setQaProductOpen(false);
    setQaProductBarcode('');
    setQaProductForm({ name: '', mrp: '', cost_price: '', qty: '1', discount: '' });
    setQaDiscountMode('percent');
    setQaDiscountRs('');
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    refocusSearch();
  };

  // Temp catalog entries created offline — carried into the queue entry for sync
  const [tempProducts, setTempProducts] = useState([]);

  // Overall discount
  const [overallDiscount, setOverallDiscount]       = useState('');
  const [overallDiscountType, setOverallDiscountType] = useState('percent');

  // Payment modal
  const [paymentOpen, setPaymentOpen]         = useState(false);
  const [selectedMode, setSelectedMode]       = useState('cash');
  const [splitMode, setSplitMode]             = useState(false);
  const [splitAmounts, setSplitAmounts]       = useState({ cash: '', card: '', upi: '', khata: '' });
  const [khataCollectAmount, setKhataCollectAmount] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const confirmBtnRef = useRef(null);
  const overallDiscountRef = useRef(null);
  const payBtnRef = useRef(null);
  const searchQueryRef = useRef('');
  const cartEndRef = useRef(null);
  const cartScrollRef = useRef(null);
  const prevCartLenRef = useRef(0);

  // Receipt
  const [receiptSale, setReceiptSale] = useState(null);

  // UI toggles
  const [bannerDismissed, setBannerDismissed] = useState(() => !!sessionStorage.getItem('pos_banner_dismissed'));
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // True while any in-page modal (or the receipt) is open — used to route Escape and to
  // gate the click-to-refocus catch-all.
  const anyModalOpen = paymentOpen || quickAddOpen || qaProductOpen || !!receiptSale || shortcutsOpen || orderImportOpen;
  // Narrower than anyModalOpen: excludes the receipt, which has no input fields of its own
  // to keep focus. Used only to drive the blur/refocus effect below, so a scan can land in
  // the search box even while the receipt from the just-completed sale is still on screen.
  const modalNeedsFocus = paymentOpen || quickAddOpen || qaProductOpen || shortcutsOpen || orderImportOpen;

  // Amendment state
  const amendSale = location.state?.amendSale || null;

  // Multi-cart slots — 3 independent carts (Customer 1 / 2 / 3)
  const [activeSlot, setActiveSlot] = useState(0);
  const slotsRef = useRef(makeEmptySlots());
  const catalogChannelRef = useRef(null); // held so blur handlers can send MRP broadcasts
  // Prevents persistence effect from overwriting hydration-populated slotsRef with stale
  // initial state on the first render (before hydration's setState calls propagate).
  const firstPersistenceRunRef = useRef(true);
  // Timestamp the tab went hidden — used to detect a long sleep/background gap on wake.
  const hiddenAtRef = useRef(null);


  // Safety net for the rare case a catalog_changed/stock_changed broadcast was
  // missed (e.g. tab was backgrounded when it fired): force a full re-fetch on
  // tab focus, but only if the catalog hasn't synced in a while. Price/stock
  // staleness in the common case is already handled by the Realtime broadcasts
  // above, so this doesn't need to fire on every alt-tab.
  //
  // A long sleep/background gap (laptop closed, tab discarded, phone locked)
  // gets stronger treatment: the auth session may need a proactive refresh
  // (supabase-js refreshes an expired cached session on getSession()), and the
  // catalog is force-refreshed regardless of the 30-min staleness check below —
  // this is the automatic version of what a manual hard-reload used to fix.
  useEffect(() => {
    const THIRTY_MIN = 30 * 60 * 1000;
    const LONG_SLEEP = 10 * 60 * 1000;
    const onVisible = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      if (document.visibilityState !== 'visible') return;
      const hiddenFor = hiddenAtRef.current ? Date.now() - hiddenAtRef.current : 0;
      hiddenAtRef.current = null;
      if (hiddenFor >= LONG_SLEEP) {
        // getSession() is cheap (auth-only, no catalog payload) and lets supabase-js
        // proactively refresh an expired cached session. Only force the *full*
        // catalog re-fetch below if it's actually owed — either a previous sync
        // attempt genuinely failed (isProductsDirty, see the retry logic further
        // down) or the plain 30-min cache is stale anyway. A long sleep with an
        // already-successfully-synced catalog costs nothing extra here.
        supabase.auth.getSession().finally(() => {
          if (isProductsDirty() || isProductSyncStale(THIRTY_MIN)) {
            markProductsDirty();
            setPosRefreshKey(k => k + 1);
          }
        });
        return;
      }
      if (isProductSyncStale(THIRTY_MIN)) {
        markProductsDirty();
        setPosRefreshKey(k => k + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Camera pre-warming on mobile: start getUserMedia early so the scanner overlay
  // opens in <0.3 s instead of waiting 2-3 s for hardware negotiation.
  useEffect(() => {
    if (!isMobile) return;
    navigator.mediaDevices?.getUserMedia({
      video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } },
    }).then(s => { preWarmStreamRef.current = s; }).catch(() => {});
    return () => {
      preWarmStreamRef.current?.getTracks().forEach(t => t.stop());
      preWarmStreamRef.current = null;
    };
  }, [isMobile]); // eslint-disable-line react-hooks/exhaustive-deps


  // ── Real-time khata balance sync (cross-device) ───────────────────────────
  // Backend broadcasts a 'khata_changed' event whenever any device modifies a
  // customer's balance. This keeps every logged-in PC in sync instantly.
  useEffect(() => {
    const storeId = user?.store_id;
    if (!storeId) return;

    const channel = supabase.channel(`khata-${storeId}`)
      .on('broadcast', { event: 'khata_changed' }, ({ payload }) => {
        const { customer_id, new_balance } = payload ?? {};
        if (!customer_id) return;
        const bal = parseFloat(new_balance ?? 0);
        setCustomers(prev =>
          prev.map(c => c.id === customer_id ? { ...c, khata_balance: bal } : c)
        );
        setCustomer(curr =>
          curr?.id === customer_id ? { ...curr, khata_balance: bal } : curr
        );
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.store_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time cross-device catalog price sync — mirrors the khata channel above.
  useEffect(() => {
    const storeId = user?.store_id;
    if (!storeId) return;

    const channel = supabase.channel(`catalog-${storeId}`)
      .on('broadcast', { event: 'catalog_changed' }, ({ payload }) => {
        const { catalog_id, mrp, ...otherFields } = payload ?? {};
        if (!catalog_id) return;
        const hasPrice = mrp != null;
        const sp = hasPrice ? parseFloat(mrp) : null;

        setProducts(prev => {
          const next = prev.map(p =>
            p.catalog_id === catalog_id
              ? { ...p, ...otherFields, ...(hasPrice ? { mrp: sp } : {}) }
              : p
          );
          const updated = next.find(p => p.catalog_id === catalog_id);
          if (updated) putProducts([updated]).catch(() => {});
          return next;
        });

        // Price changes need cart reconciliation; other field edits (name, barcode,
        // category, etc.) don't affect an already-priced cart line.
        if (!hasPrice) return;
        const syntheticProds = [{ catalog_id, mrp: sp }];
        slotsRef.current.forEach(slot => {
          slot.cart = slot.cart.map(item => reconcileCartItem(item, syntheticProds));
        });
        setCart(prevCart => {
          const next = prevCart.map(item => reconcileCartItem(item, syntheticProds));
          const changed = next.some((item, i) => item !== prevCart[i]);
          if (changed) toast.info('Cart prices updated — price changed on another device');
          return changed ? next : prevCart;
        });
      })
      // Backend pushes this after a sale/purchase/stock-adjustment on any device
      // instead of us re-fetching the whole catalog (see helpers/realtime.py).
      .on('broadcast', { event: 'stock_changed' }, ({ payload }) => {
        const items = payload?.items;
        if (!Array.isArray(items) || !items.length) return;
        const stockMap = new Map(items.map(i => [i.catalog_id, i.stock]));

        setProducts(prev => {
          const touched = [];
          const next = prev.map(p => {
            if (!stockMap.has(p.catalog_id)) return p;
            const merged = { ...p, stock: stockMap.get(p.catalog_id) };
            touched.push(merged);
            return merged;
          });
          if (touched.length) putProducts(touched).catch(() => {});
          return next;
        });
      })
      // A product created on another device/tab — insert it locally so it's
      // immediately searchable/scannable without waiting for the periodic sync.
      .on('broadcast', { event: 'catalog_created' }, ({ payload }) => {
        const { catalog_id, ...fields } = payload ?? {};
        if (!catalog_id) return;
        setProducts(prev => {
          if (prev.some(p => p.catalog_id === catalog_id)) return prev; // e.g. this tab created it
          const newProduct = { catalog_id, stock: 0, ...fields };
          putProducts([newProduct]).catch(() => {});
          return [...prev, newProduct];
        });
      })
      // A product deleted or merged away on another device/tab.
      .on('broadcast', { event: 'catalog_deleted' }, ({ payload }) => {
        const { catalog_id } = payload ?? {};
        if (!catalog_id) return;
        setProducts(prev => prev.filter(p => p.catalog_id !== catalog_id));
        removeProduct(catalog_id).catch(() => {});
      })
      .subscribe();

    catalogChannelRef.current = channel;
    return () => {
      catalogChannelRef.current = null;
      supabase.removeChannel(channel);
    };
  }, [user?.store_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load data ─────────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Step 1: IndexedDB — always instant
      const [rawCachedProds, cachedCusts] = await Promise.all([
        getAllProducts(),
        getAllCustomers(),
      ]);

      // Reject stale cross-store cache (e.g. admin switching impersonation
      // sessions). If the cached products were stamped for a different store_id
      // we treat the cache as empty and force a fresh API call.
      const currentStoreId = storeProfile?.store_id ?? user?.store_id;
      const cachedStoreId  = getProductsStoreId();
      const storeMatch = !currentStoreId || !cachedStoreId || cachedStoreId === currentStoreId;
      const cachedProds = storeMatch ? rawCachedProds : [];

      const dirty = isProductsDirty();
      // On a bare remount (e.g. navigating back to /pos), only re-validate
      // against the network if the cache is actually stale — stock is kept
      // fresh via broadcast and catalog edits already set `dirty`.
      const shouldRefetch = dirty || (posRefreshKey === 0 && isProductSyncStale(30 * 60 * 1000));

      // Fast path: returning from Ctrl+Shift+M price edit. The specific product's
      // new mrp is in navigation state — apply it directly and skip the
      // full /pos/products API call entirely.
      const patched = location.state?.patchedProduct ?? null;
      if (patched && !dirty && cachedProds.length > 0) {
        const updatedProds = cachedProds.map(p =>
          p.catalog_id === patched.catalog_id ? { ...p, name: patched.name ?? p.name, mrp: patched.mrp, default_discount: patched.default_discount ?? p.default_discount } : p
        );
        const updatedEntry = updatedProds.find(p => p.catalog_id === patched.catalog_id);
        if (updatedEntry) putProducts([updatedEntry]).catch(() => {});
        navigate('/pos', { replace: true, state: {} });
        if (!cancelled) {
          setProducts(updatedProds);
          setCustomers(cachedCusts);
          setUsingCachedData(false);
          setLoadingProducts(false);
          setCatalogRefreshing(false);
          slotsRef.current.forEach(slot => {
            slot.cart = slot.cart.map(item => reconcileCartItem(item, updatedProds));
          });
          setCart(prevCart => {
            const next = prevCart.map(item => reconcileCartItem(item, updatedProds));
            const changed = next.some((item, i) => item !== prevCart[i]);
            if (changed) toast.info('Cart prices updated to reflect catalog changes');
            return changed ? next : prevCart;
          });
          if (!isMobile) setTimeout(() => searchRef.current?.focus(), 0);
        }
        return;
      }

      if (!cancelled) {
        if (cachedProds.length > 0) {
          setProducts(cachedProds);
          setCustomers(cachedCusts);
          setUsingCachedData(true);
          setLoadingProducts(false);
          if (shouldRefetch) setCatalogRefreshing(true);
        } else {
          setLoadingProducts(true);
        }
      }

      // Step 2a: Always refresh customers from API — khata balance must be current.
      // Fire-and-forget with offline fallback; preserves temp (offline-created) customers.
      posAPI.listCustomers({ limit: 500 })
        .then(r => {
          if (cancelled) return;
          const custs = r.data?.items ?? [];
          putCustomers(custs).catch(() => {});
          setCustomers(prev => {
            const temps = prev.filter(c => typeof c.id === 'string' && c.id.startsWith('TMP-CUST-'));
            return temps.length ? [...custs, ...temps] : custs;
          });
          // For supplier users, override khata_balance with the authoritative credit balance
          // (acknowledged invoices − collected). customers.khata_balance tracks the retailer's
          // own internal khata, not what they owe the supplier.
          if (user?.role === 'supplier') {
            supplierAPI.getCredit()
              .then(cr => {
                if (cancelled) return;
                const creditMap = Object.fromEntries(
                  (cr.data?.stores ?? []).map(s => [s.store_id, s.balance])
                );
                setCustomers(prev =>
                  prev.map(c => {
                    const linked = c.kiranaflow_store_id;
                    if (!linked || creditMap[linked] === undefined) return c;
                    return { ...c, khata_balance: creditMap[linked] };
                  })
                );
              })
              .catch(() => {});
          }
        })
        .catch(() => {}); // offline — IndexedDB cache already set in state above

      // Step 2b: API call when cache is empty or a refetch is actually warranted.
      if (cachedProds.length === 0 || shouldRefetch) {
        // Retries a couple of times before giving up — a refresh triggered right
        // after the tab wakes from a long sleep can land on a still-flaky
        // connection. Only mark clean/synced once a fetch actually succeeds, so
        // a fully-exhausted retry leaves the dirty flag set for the next trigger.
        let prods = null;
        for (let attempt = 0; attempt <= 2 && prods === null; attempt++) {
          if (attempt > 0) {
            if (!navigator.onLine) break; // definitely offline — retrying is pointless, fall through to cached data
            await new Promise(r => setTimeout(r, attempt === 1 ? 1500 : 3000));
          }
          try {
            prods = await posAPI.getProducts().then(r => r.data?.items ?? []);
          } catch {
            // give up after the loop below exits (either exhausted or offline-break)
          }
        }
        if (prods === null) {
          if (!cancelled) {
            setLoadingProducts(false);
            setCatalogRefreshing(false);
            if (cachedProds.length === 0) {
              toast.error('No cached products. Connect to the internet to load products.');
            } else {
              toast.warning("Couldn't refresh products — showing cached data, will retry.");
            }
          }
        } else {
          markProductsClean();
          replaceAllProducts(prods).catch(() => {});
          if (currentStoreId) setProductsStoreId(currentStoreId);
          markProductsSynced();
          if (!cancelled) {
            setProducts(prods);
            setUsingCachedData(false);
            setLoadingProducts(false);
            setCatalogRefreshing(false);

            // When a catalog edit triggered this refresh (dirty flag), sync any
            // stale prices in the cart. This covers items already in the cart
            // before the edit AND items scanned during the brief refresh window.
            if (dirty) {
              // Reconcile inactive slots stored in the ref (never in React state)
              slotsRef.current.forEach(slot => {
                slot.cart = slot.cart.map(item => reconcileCartItem(item, prods));
              });
              // Reconcile the active cart (React state) via functional update
              setCart(prevCart => {
                const next = prevCart.map(item => reconcileCartItem(item, prods));
                const changed = next.some((item, i) => item !== prevCart[i]);
                if (changed) toast.info('Cart prices updated to reflect catalog changes');
                return changed ? next : prevCart;
              });
            }
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [posRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // OfflineContext's global 5-min background sync (and reconnect sync) writes fresh
  // catalog data straight to IndexedDB but has no way to reach an already-mounted
  // POS page's live `products` state on its own — only this page's own 30-min/
  // long-sleep triggers above do that. Bridge the two: whenever that background
  // sync stamps a new lastSyncedAt, pull the already-fresh IndexedDB data into
  // view. No extra network call — OfflineContext already did the fetch.
  const lastSyncedAtRef = useRef(null);
  useEffect(() => {
    const prev = lastSyncedAtRef.current;
    lastSyncedAtRef.current = lastSyncedAt;
    if (prev === null || lastSyncedAt == null || lastSyncedAt === prev) return;
    getAllProducts().then(fresh => {
      if (fresh.length > 0) setProducts(fresh);
    }).catch(() => {});
  }, [lastSyncedAt]);

  // Pre-fill cart when amending
  useEffect(() => {
    if (!amendSale) return;
    if (!isOnline) {
      toast.warning('Bill amendments are not available offline.');
      navigate('/pos', { replace: true, state: {} });
      setTimeout(() => refocusSearch(), 0);
      return;
    }
    const preCart = (amendSale.items || []).map(item => {
      const sellingPrice = item.mrp ?? item.unit_price ?? 0;
      const disc         = item.discount ?? 0;
      const discType     = item.discount_type ?? 'percent';
      const unitPrice    = item.unit_price ?? sellingPrice;
      const total        = item.item_total ?? Math.round(item.quantity * unitPrice * 100) / 100;
      const gstRate      = item.gst_rate ?? 0;
      return {
        catalog_id:    item.catalog_id,
        product_name:  item.product_name,
        qty:           item.quantity,
        mrp: sellingPrice,
        discount:      disc,
        discount_type: discType,
        unit_price:    unitPrice,
        gst_rate:      gstRate,
        item_total:    total,
        gst_amount:    calcGst(total, gstRate),
      };
    });
    setCart(preCart);

    // Restore overall discount — DB stores the computed amount; reverse-calculate
    // the percent when discount_type is "percent" so the input field shows "10" not "6.84"
    const odAmount = amendSale.overall_discount ?? 0;
    const odType   = amendSale.overall_discount_type ?? 'amount';
    if (odAmount > 0) {
      setOverallDiscountType(odType);
      if (odType === 'percent') {
        const sub = amendSale.subtotal ?? (amendSale.items || []).reduce((s, i) => s + (i.item_total ?? 0), 0);
        const pct = sub > 0 ? Math.round(odAmount / sub * 10000) / 100 : odAmount;
        setOverallDiscount(String(pct));
      } else {
        setOverallDiscount(String(odAmount));
      }
    }

    // Pre-fill customer so khata UI is visible and linked customer isn't lost
    if (amendSale.customer_id) {
      customersAPI.get(amendSale.customer_id)
        .then(r => {
          setCustomer(r.data);
          setCustomerSearch(r.data.name || '');
        })
        .catch(() => {
          // Customer may have been deleted — show name only for reference
          if (amendSale.customer_name) setCustomerSearch(amendSale.customer_name);
        });
    }

    // Pre-fill previously collected khata so user can review / adjust
    if ((amendSale.khata_cleared ?? 0) > 0) {
      setKhataCollectAmount(String(amendSale.khata_cleared));
    }

    toast.info(`Editing bill ${amendSale.bill_number} — make changes and checkout`);
    setTimeout(() => refocusSearch(), 0);
  }, [amendSale]);

  // Hydrate all slots from sessionStorage on mount (skipped in amendment mode)
  useEffect(() => {
    if (amendSale) return;
    try {
      // Migrate old single-cart key into slot 0
      const oldRaw = sessionStorage.getItem('kiranaflow_pos_cart');
      if (oldRaw) {
        const old = JSON.parse(oldRaw);
        slotsRef.current[0] = {
          cart: old.cart || [],
          tempProducts: [],
          customer: old.customer || null,
          overallDiscount: old.overallDiscount || '',
          overallDiscountType: old.overallDiscountType || 'percent',
        };
        sessionStorage.removeItem('kiranaflow_pos_cart');
      }
      const raw = sessionStorage.getItem('kiranaflow_pos_slots');
      if (!raw) {
        // Restore slot 0 if it was just migrated
        const s = slotsRef.current[0];
        if (s.cart?.length) {
          setCart(s.cart);
          lastAddedCatalogIdRef.current = s.cart[s.cart.length - 1].catalog_id;
        }
        if (s.customer) { setCustomer(s.customer); setCustomerSearch(s.customer.name || ''); }
        if (s.overallDiscount) setOverallDiscount(s.overallDiscount);
        if (s.overallDiscountType) setOverallDiscountType(s.overallDiscountType);
        return;
      }
      const { activeSlot: savedSlot, slots } = JSON.parse(raw);
      if (Array.isArray(slots)) {
        slots.forEach((s, i) => { if (s) slotsRef.current[i] = s; });
      }
      const slotIdx = [0, 1, 2].includes(savedSlot) ? savedSlot : 0;
      const s = slotsRef.current[slotIdx];
      setActiveSlot(slotIdx);
      if (s.cart?.length) {
        setCart(s.cart);
        lastAddedCatalogIdRef.current = s.cart[s.cart.length - 1].catalog_id;
      }
      if (s.customer) { setCustomer(s.customer); setCustomerSearch(s.customer.name || ''); }
      if (s.overallDiscount) setOverallDiscount(s.overallDiscount);
      if (s.overallDiscountType) setOverallDiscountType(s.overallDiscountType);
      if (s.tempProducts?.length) setTempProducts(s.tempProducts);
    } catch {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fresh page load with no amendment — ready the search bar for scanning.
  useEffect(() => {
    if (!isMobile && !amendSale) setTimeout(() => refocusSearch(), 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist all slots to sessionStorage whenever active slot state changes
  useEffect(() => {
    if (amendSale) return;
    try {
      if (firstPersistenceRunRef.current) {
        // First run fires with stale initial state (cart=[], etc.) before hydration's
        // setState calls propagate. slotsRef already has correct data from the hydration
        // effect which ran just before this. Don't overwrite it — just save as-is.
        firstPersistenceRunRef.current = false;
        const hasAnyData = slotsRef.current.some(s => s.cart.length > 0 || s.customer || s.overallDiscount);
        if (hasAnyData) {
          sessionStorage.setItem('kiranaflow_pos_slots', JSON.stringify({ activeSlot, slots: slotsRef.current }));
        }
        return;
      }
      slotsRef.current[activeSlot] = { cart, tempProducts, customer, overallDiscount, overallDiscountType };
      const hasAnyData = slotsRef.current.some(s => s.cart.length > 0 || s.customer || s.overallDiscount);
      if (!hasAnyData) {
        sessionStorage.removeItem('kiranaflow_pos_slots');
      } else {
        sessionStorage.setItem('kiranaflow_pos_slots', JSON.stringify({
          activeSlot,
          slots: slotsRef.current,
        }));
      }
    } catch {}
  }, [cart, customer, overallDiscount, overallDiscountType, tempProducts, activeSlot]); // eslint-disable-line react-hooks/exhaustive-deps


  // Scroll cart to bottom whenever a new item is added
  useEffect(() => {
    if (cart.length > prevCartLenRef.current) {
      cartEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    prevCartLenRef.current = cart.length;
  }, [cart.length]);

  // ── Slot switching ───────────────────────────────────────────────────────

  const switchSlot = useCallback((newIdx) => {
    if (newIdx === activeSlot) return;
    slotsRef.current[activeSlot] = { cart, tempProducts, customer, overallDiscount, overallDiscountType };
    const s = slotsRef.current[newIdx];
    setCart(s.cart);
    lastAddedCatalogIdRef.current = s.cart?.length ? s.cart[s.cart.length - 1].catalog_id : null;
    setTempProducts(s.tempProducts);
    setCustomer(s.customer);
    setCustomerSearch(s.customer?.name || '');
    setOverallDiscount(s.overallDiscount);
    setOverallDiscountType(s.overallDiscountType);
    setActiveSlot(newIdx);
    setRetailerGateError(null);
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    setShowCustomerDrop(false);
    refocusSearch();
  }, [activeSlot, cart, tempProducts, customer, overallDiscount, overallDiscountType, refocusSearch]);

  // ── Global keyboard shortcuts ─────────────────────────────────────────────

  useEffect(() => {
    const handler = (e) => {
      // When any modal/popup is open, keyboard events must not reach background shortcuts.
      // paymentOpen and receiptSale have their own dedicated window handlers for Escape.
      // quickAdd and qaProduct modals have no dedicated handler, so we handle Escape here.
      if (anyModalOpen) {
        if (e.key === 'Escape') {
          if (quickAddOpen) setQuickAddOpen(false);
          if (qaProductOpen) closeQaModal();
          if (shortcutsOpen) setShortcutsOpen(false);
          if (orderImportOpen) closeOrderImport();
        }
        return;
      }
      // Restrict Tab to the defined cycle: search ↔ Qty[i] ↔ MRP[i] ↔ … ↔ search
      // Any Tab press outside those elements is absorbed and redirected to search.
      if (e.key === 'Tab') {
        const active = document.activeElement;
        const inCycle =
          active === searchRef.current ||
          qtyInputRefs.current.some(el => el && el === active) ||
          spInputRefs.current.some(el => el && el === active) ||
          discountInputRefs.current.some(el => el && el === active) ||
          upInputRefs.current.some(el => el && el === active) ||
          trashInputRefs.current.some(el => el && el === active);
        if (!inCycle) {
          e.preventDefault();
          searchRef.current?.focus();
          return;
        }
      }
      if (e.key === '/' && document.activeElement !== searchRef.current) {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setShowDropdown(false);
      }
      if ((e.ctrlKey && e.key === 'Enter') || e.key === 'F9') {
        if (cart.length > 0) {
          e.preventDefault();
          if (user?.role === 'supplier') { handleCheckout('khata'); } else { setPaymentOpen(true); }
        }
      }
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      const inInput = activeTag === 'input' || activeTag === 'textarea' || activeTag === 'select';
      if (e.altKey && e.key === 'c' && cart.length > 0) {
        e.preventDefault();
        setQuickAddOpen(true);
      }
      if (e.altKey && e.key === 'p') {
        e.preventDefault();
        setQaProductForm({ name: '', mrp: '', cost_price: '', qty: '1', discount: '' });
        setQaProductBarcode('');
        setQaDiscountMode('percent');
        setQaDiscountRs('');
        setQaProductOpen(true);
        setShowDropdown(false);
      }
      if (!isMobile) {
        if (e.ctrlKey && ['1', '2', '3'].includes(e.key)) {
          if (amendSale) return;
          e.preventDefault();
          switchSlot(parseInt(e.key) - 1);
        }
        if (e.ctrlKey && e.key === 'ArrowRight') {
          if (amendSale) return;
          e.preventDefault();
          switchSlot((activeSlot + 1) % 3);
        }
        if (e.ctrlKey && e.key === 'ArrowLeft') {
          if (amendSale) return;
          e.preventDefault();
          switchSlot((activeSlot + 2) % 3);
        }
      }
      if (e.ctrlKey && e.shiftKey && e.key === 'M') {
        e.preventDefault();
        const lastItem = cart[cart.length - 1];
        if (!lastItem) {
          toast.info('Add a product to cart first');
          return;
        }
        if (!lastItem.catalog_id || lastItem.catalog_id.startsWith('TMP-')) {
          toast.info('This product cannot be edited in Stock Master yet');
          return;
        }
        navigate('/appendix', {
          state: {
            tab:            'stock',
            catalogId:      lastItem.catalog_id,
            focusSellPrice: true,
            currentMrp:     lastItem.mrp,
          },
        });
      }
      if (e.altKey && e.key.toLowerCase() === 'q') {
        e.preventDefault();
        const idx = cart.findIndex(item => item.catalog_id === lastAddedCatalogIdRef.current);
        if (idx >= 0 && qtyInputRefs.current[idx]) {
          qtyInputRefs.current[idx].focus();
          qtyInputRefs.current[idx].select();
        }
      }
      if (e.altKey && e.key.toLowerCase() === 'u') {
        e.preventDefault();
        const idx = cart.findIndex(item => item.catalog_id === lastAddedCatalogIdRef.current);
        if (idx >= 0 && upInputRefs.current[idx]) {
          upInputRefs.current[idx].focus();
          upInputRefs.current[idx].select();
        }
      }
      if (e.altKey && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        const idx = cart.findIndex(item => item.catalog_id === lastAddedCatalogIdRef.current);
        if (idx >= 0 && spInputRefs.current[idx]) {
          mrpOriginalValueRef.current = cart[idx]?.mrp ?? null;
          spInputRefs.current[idx].focus();
          spInputRefs.current[idx].select();
          mrpSaveModeIdxRef.current = idx;
          setMrpSaveModeIdx(idx);
        }
      }
      if (e.altKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        const idx = cart.findIndex(item => item.catalog_id === lastAddedCatalogIdRef.current);
        if (idx >= 0 && discountInputRefs.current[idx]) {
          discountInputRefs.current[idx].focus();
          discountInputRefs.current[idx].select();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [cart, anyModalOpen, quickAddOpen, qaProductOpen, shortcutsOpen, orderImportOpen, switchSlot, activeSlot, amendSale, isMobile]);

  // Payment modal keyboard: ←→ / 1-4 select mode, Enter confirm, Esc close
  useEffect(() => {
    if (!paymentOpen) return;
    const handler = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      const focusedInInput = tag === 'input' || tag === 'textarea';
      if (e.key === 'Escape') { setPaymentOpen(false); return; }
      if (focusedInInput) return;
      if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        setSplitMode(m => {
          if (m) setSplitAmounts({ cash: '', card: '', upi: '', khata: '' });
          return !m;
        });
        return;
      }
      if (splitMode) return;
      if (['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        const curIdx = PAYMENT_MODES.findIndex(m => m.key === selectedMode);
        const row = Math.floor(curIdx / 2);
        const col = curIdx % 2;
        let nextIdx = curIdx;
        if (e.key === 'ArrowLeft'  && col > 0) nextIdx = curIdx - 1;
        if (e.key === 'ArrowRight' && col < 1) nextIdx = curIdx + 1;
        if (e.key === 'ArrowUp'    && row > 0) nextIdx = curIdx - 2;
        if (e.key === 'ArrowDown'  && row < 1) nextIdx = curIdx + 2;
        const target = PAYMENT_MODES[nextIdx];
        if (target && !(target.key === 'khata' && !customer)) {
          setSelectedMode(target.key);
        }
      }
      if (['1', '2', '3', '4'].includes(e.key)) {
        e.preventDefault();
        const idx = parseInt(e.key) - 1;
        if (idx < PAYMENT_MODES.length) {
          const mode = PAYMENT_MODES[idx].key;
          if (mode === 'khata' && !customer) return;
          setSelectedMode(mode);
        }
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        confirmBtnRef.current?.click();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [paymentOpen, customer, selectedMode, splitMode]);

  // Hard rule: when a modal that owns keyboard input is open, the search bar must not have
  // focus. When it closes, return focus to search for the next scan. The receipt is
  // deliberately excluded (see modalNeedsFocus) — it has no input fields, so a scan can
  // land in the search box even while it's still on screen. On mobile the camera is the
  // default; skip search auto-focus entirely.
  const wasModalOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = wasModalOpenRef.current;
    wasModalOpenRef.current = modalNeedsFocus;

    if (modalNeedsFocus) {
      searchRef.current?.blur();
      setShowDropdown(false);
    } else if (wasOpen && !isMobile) {
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }

    // When the payment modal opens, pull focus away from any background element
    // (e.g. quantity input) and seat it on the confirm button so Enter works immediately.
    if (paymentOpen) {
      document.activeElement?.blur();
      const t = setTimeout(() => confirmBtnRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [modalNeedsFocus, paymentOpen, isMobile]);

  // Returning from Ctrl+Shift+M via Escape (cancel): no patchedProduct in state,
  // but fromStockMaster flag tells us to refocus the search bar.
  useEffect(() => {
    if (!location.state?.fromStockMaster || location.state?.patchedProduct) return;
    navigate('/pos', { replace: true, state: {} });
    if (!isMobile) setTimeout(() => searchRef.current?.focus(), 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Product search ────────────────────────────────────────────────────────

  const doSearch = useCallback((q) => {
    if (!q.trim()) { setSearchResults([]); setShowDropdown(false); return; }
    const lower = q.toLowerCase();
    const scoreProduct = (p) => {
      const bc   = String(p.barcode || '').toLowerCase();
      const name = (p.name || p.product_name || '').toLowerCase();
      if (bc === lower)            return 0; // exact barcode
      if (bc.startsWith(lower))   return 1; // barcode prefix
      if (name.startsWith(lower)) return 2; // name prefix
      if (bc.includes(lower))     return 3; // barcode contains
      return 4;                             // name contains
    };
    const results = products
      .filter(p => {
        const name = (p.name || p.product_name || '').toLowerCase();
        return name.includes(lower) ||
          String(p.barcode || '').toLowerCase().includes(lower);
      })
      .sort((a, b) => scoreProduct(a) - scoreProduct(b))
      .slice(0, 20);
    setSearchResults(results);
    // Auto-highlight first result so Enter adds it immediately; ArrowDown moves to second.
    // When no results, point at Quick Add row (index 0 = searchResults.length = 0).
    // Exception: never auto-highlight a barcode that merely starts with or contains the
    // typed text (tiers 1/3) — only a genuine identity match (exact barcode, tier 0) or an
    // ordinary name search (tiers 2/4) is safe to auto-select. A scanner misread (e.g. a
    // dropped digit) can otherwise fuzzy-match a different product's barcode and silently
    // ring up the wrong item on Enter; leaving nothing selected keeps it visible for an
    // explicit pick instead.
    const topScore = results.length > 0 ? scoreProduct(results[0]) : null;
    const unsafeAutoSelect = topScore === 1 || topScore === 3;
    setSelectedIdx(unsafeAutoSelect ? -1 : 0);
    // Always show dropdown when there's a query — the Quick Add row is always present
    setShowDropdown(true);
  }, [products]);

  // Re-run the active search query whenever products finish loading (fixes first-load race condition)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (products.length > 0 && searchQueryRef.current.trim()) {
      doSearch(searchQueryRef.current);
    }
  }, [products]);

  // The dropdown renders absolutely-positioned over whatever is below the search bar
  // (usually the top rows of the cart), so a click meant for a cart control — e.g. a
  // row's trash button — can land on the dropdown overlay instead and appear to do
  // nothing. Close the dropdown the instant a click starts outside it, before the
  // browser resolves the click target, so the cart is clickable again immediately.
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e) => {
      if (searchRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showDropdown]);

  // Scroll the highlighted dropdown item into view on keyboard navigation
  useEffect(() => {
    if (!dropdownRef.current) return;
    const buttons = dropdownRef.current.querySelectorAll('button');
    if (buttons[selectedIdx]) {
      buttons[selectedIdx].scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIdx]);

  // ── Customer ──────────────────────────────────────────────────────────────

  const filteredCustomers = useMemo(() => {
    const seen = new Set();
    const unique = customers.filter(c => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
    if (!customerSearch.trim()) return unique.slice(0, 20);
    const lower = customerSearch.toLowerCase();
    const query = customerSearch.trim();
    const scored = [];
    for (const c of unique) {
      const code = c.account_code != null ? String(c.account_code) : '';
      const name = c.name?.toLowerCase() ?? '';
      let score = Infinity;
      if (code === query)              score = 0;
      else if (code.startsWith(query)) score = 1;
      else if (name.startsWith(lower)) score = 2;
      else if (code.includes(query))   score = 3;
      else if (name.includes(lower))   score = 4;
      else if (c.mobile?.includes(query)) score = 5;
      else continue;
      scored.push({ ...c, _score: score });
    }
    scored.sort((a, b) => a._score - b._score || (a.name ?? '').localeCompare(b.name ?? ''));
    return scored.slice(0, 10).map(({ _score, ...c }) => c);
  }, [customers, customerSearch]);

  useEffect(() => {
    if (!customerDropdownRef.current) return;
    const buttons = customerDropdownRef.current.querySelectorAll('button');
    // Layout: [QuickAdd at pos 0, customers[n-1]..customers[0] at pos n]
    // buttonPos = filteredCustomers.length - customerSelectedIdx
    const buttonPos = filteredCustomers.length - customerSelectedIdx;
    if (buttons[buttonPos]) {
      buttons[buttonPos].scrollIntoView({ block: 'nearest' });
    }
  }, [customerSelectedIdx, filteredCustomers.length]);

  // Runs synchronously on every keystroke (no debounce) so the dropdown — and therefore
  // Enter's selection — is always correct by the time the *next* keydown (e.g. a scanner's
  // terminating Enter, which can arrive faster than any debounce window) is dispatched.
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    searchQueryRef.current = val;
    doSearch(val);
  };

  const handleSearchKeyDown = (e) => {
    // Ctrl/Cmd+Enter is the checkout shortcut — never treat it as "commit search selection".
    // Let it bubble untouched to the window-level checkout handler.
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') return;
    // Tab forward: jump to first cart qty (cycle; do nothing if cart empty)
    if (e.key === 'Tab' && !e.shiftKey && !showDropdown) {
      if (cart.length > 0) {
        e.preventDefault();
        cartScrollRef.current?.scrollTo({ top: 0 });
        qtyInputRefs.current[0]?.focus();
        qtyInputRefs.current[0]?.select();
      } else {
        e.preventDefault(); // stay in search when cart is empty
      }
      return;
    }
    // Shift+Tab backward: wrap to last row's Trash button (close the cycle)
    if (e.key === 'Tab' && e.shiftKey && !showDropdown) {
      e.preventDefault();
      if (cart.length > 0) {
        trashInputRefs.current[cart.length - 1]?.focus();
      }
      return;
    }
    // Tab while the dropdown is open: cycle forward through results + Quick Add row,
    // then wrap back to the search box (nothing highlighted) instead of leaking focus
    // onto the dropdown's native <button> elements. Arrow keys keep their clamped
    // (non-wrapping) behaviour above/below — this just adds Tab as an alternative.
    if (e.key === 'Tab' && !e.shiftKey && showDropdown) {
      e.preventDefault();
      setSelectedIdx(i => (i >= searchResults.length ? -1 : i + 1));
      return;
    }
    if (e.key === 'Tab' && e.shiftKey && showDropdown) {
      e.preventDefault();
      setSelectedIdx(i => (i <= -1 ? searchResults.length : i - 1));
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showDropdown && searchQuery.trim() && searchResults.length > 0) { setShowDropdown(true); setSelectedIdx(0); }
      // Allow going one past results to select the Quick Add row
      else setSelectedIdx(i => Math.min(i + 1, searchResults.length));
      return;
    }
    if (!showDropdown) {
      if (e.key === 'Enter' && searchQuery.trim()) {
        e.preventDefault();
        // Rare fallback (dropdown normally already reflects every keystroke) — e.g.
        // stale text left in the box with the dropdown closed, then Enter with no
        // new typing first. Just (re)open it; a second Enter completes the selection.
        doSearch(searchQuery.trim());
      }
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIdx >= 0 && selectedIdx < searchResults.length) {
        addToCart(searchResults[selectedIdx]);
      } else if (selectedIdx === searchResults.length) {
        // Quick Add row is highlighted (or no results — auto-lands here)
        const trimmed = searchQuery.trim();
        if (looksLikeCorruptedBarcode(trimmed)) {
          toast.warning('This code doesn’t look like a complete barcode — check it before saving, or try scanning again.');
        }
        openQaModal(trimmed);
      }
    }
  };

  // ── Cart management ───────────────────────────────────────────────────────

  const addToCart = useCallback((product, opts = {}) => {
    const { fromBarcode = false, fromMouse = false } = opts;
    const price = product.mrp ?? 0;
    const gst   = product.gst_rate ?? 0;
    setCart(prev => {
      const existing = prev.findIndex(c => c.catalog_id === product.catalog_id);
      const targetIdx = existing >= 0 ? existing : prev.length;
      lastAddedCatalogIdRef.current = product.catalog_id;
      if (existing >= 0) {
        return prev.map((item, i) => {
          if (i !== existing) return item;
          const qty       = item.qty + 1;
          const item_total = Math.round(qty * item.unit_price * 100) / 100;
          return { ...item, qty, item_total, gst_amount: calcGst(item_total, item.gst_rate) };
        });
      }
      const defaultDisc = product.default_discount ?? 0;
      const unitPrice   = calcUnitCost(price, defaultDisc, 'percent');
      const item_total  = Math.round(unitPrice * 100) / 100;
      return [...prev, {
        catalog_id:    product.catalog_id,
        product_name:  product.name || product.product_name,
        qty:           1,
        mrp: price,
        discount:      defaultDisc,
        discount_type: 'percent',
        unit_price:    unitPrice,
        gst_rate:      gst,
        item_total,
        gst_amount:    calcGst(item_total, gst),
      }];
    });
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    if ((product.stock ?? 1) <= 0) {
      toast.warning(`${product.name || product.product_name} — out of stock, sale creates deficit`);
    }
  }, []);

  // O(1) lookup map rebuilt only when the products list changes.
  const barcodeMap = useMemo(() => {
    const m = new Map();
    products.forEach(p => {
      if (p.barcode) m.set(String(p.barcode).trim().toLowerCase(), p);
      if (p.sku)     m.set(String(p.sku).trim().toLowerCase(), p);
    });
    return m;
  }, [products]);

  // Shared barcode handler — used by the camera scanner and manual Enter on an exact match
  const handleBarcodeDetected = useCallback((barcode) => {
    const bc = barcode.trim().toLowerCase();
    const match = barcodeMap.get(bc);       // O(1) instead of O(n)
    if (match) {
      setLastScannedName(match.name || match.product_name || '');
      addToCart(match, { fromBarcode: true });
      setSearchQuery('');
      setSearchResults([]);
      setShowDropdown(false);
    } else if (isOnline) {
      // Product not in stock-live — check catalog (exists but never stocked)
      purchasesAPI.lookupBarcode(barcode).then(res => {
        const p = res.data;
        setLastScannedName(p.name || '');
        addToCart(
          { catalog_id: p.catalog_id, name: p.name, mrp: p.mrp ?? 0,
            gst_rate: p.gst_rate ?? 0, stock: p.current_stock ?? 0, barcode },
          { fromBarcode: true }
        );
        setSearchQuery('');
        setSearchResults([]);
        setShowDropdown(false);
      }).catch(() => {
        // Not in catalog at all — open Quick Add modal (closes camera)
        setCameraOpen(false);
        openQaModal(barcode);
      });
    } else {
      // Offline — can't do catalog lookup, go straight to Quick Add
      setCameraOpen(false);
      openQaModal(barcode);
    }
  }, [barcodeMap, isOnline, addToCart]);

  const updateQty = (idx, qty) => {
    setCart(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const item_total = Math.round(qty * item.unit_price * 100) / 100;
      return { ...item, qty, item_total, gst_amount: calcGst(item_total, item.gst_rate) };
    }));
  };

  const updateSellingPrice = (idx, price) => {
    const sp = Math.max(0, parseFloat(price) || 0);
    setCart(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const unit_price = calcUnitCost(sp, item.discount, item.discount_type);
      const item_total = Math.round(item.qty * unit_price * 100) / 100;
      return { ...item, mrp: sp, unit_price, item_total, gst_amount: calcGst(item_total, item.gst_rate) };
    }));
  };

  const updateDiscount = (idx, discount) => {
    const d = parseFloat(discount) || 0;
    setCart(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const unit_price = calcUnitCost(item.mrp, d, item.discount_type);
      const item_total = Math.round(item.qty * unit_price * 100) / 100;
      return { ...item, discount: d, unit_price, item_total, gst_amount: calcGst(item_total, item.gst_rate) };
    }));
  };

  const updateUnitPrice = (idx, value) => {
    setCart(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const up = Math.max(0, parseFloat(value) || 0);
      const discount = item.discount_type === 'percent'
        ? (item.mrp > 0 ? Math.round((item.mrp - up) / item.mrp * 100 * 100) / 100 : 0)
        : Math.round((item.mrp - up) * 100) / 100;
      const item_total = Math.round(item.qty * up * 100) / 100;
      return { ...item, unit_price: up, discount, item_total, gst_amount: calcGst(item_total, item.gst_rate) };
    }));
  };

  const toggleDiscountType = (idx) => {
    setCart(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const newType    = item.discount_type === 'amount' ? 'percent' : 'amount';
      const unit_price = calcUnitCost(item.mrp, item.discount, newType);
      const item_total = Math.round(item.qty * unit_price * 100) / 100;
      return { ...item, discount_type: newType, unit_price, item_total, gst_amount: calcGst(item_total, item.gst_rate) };
    }));
  };

  const removeItem = (catalogId) => setCart(prev => prev.filter(item => item.catalog_id !== catalogId));

  const cartTotals = useMemo(() => {
    const subtotal_gst     = cart.reduce((s, i) => s + i.gst_amount, 0);
    const subtotal         = cart.reduce((s, i) => s + i.item_total, 0);
    const subtotal_r       = Math.round(subtotal * 100) / 100;
    const total_gst_r      = Math.round(subtotal_gst * 100) / 100;

    const discountAmt = calcDiscountAmount(subtotal_r, overallDiscount, overallDiscountType);
    const after_discount  = Math.round((subtotal_r - discountAmt) * 100) / 100;
    const rounded_total   = Math.round(after_discount);
    const round_off       = Math.round((rounded_total - after_discount) * 100) / 100;

    const total_qty = cart.reduce((s, i) => s + (Number(i.qty) || 0), 0);
    const line_item_discount_amt = Math.round(
      cart.reduce((s, i) => s + ((i.mrp ?? i.unit_price) - i.unit_price) * i.qty, 0) * 100
    ) / 100;

    return {
      subtotal:              subtotal_r,
      total_gst:             total_gst_r,
      discount_amt:          Math.round(discountAmt * 100) / 100,
      line_item_discount_amt,
      after_discount,
      round_off,
      rounded_total,
      total_qty,
      // legacy alias so payment modal logic stays intact
      total:                 rounded_total,
    };
  }, [cart, overallDiscount, overallDiscountType]);



  const handleQuickAdd = async () => {
    if (!quickName.trim()) return;
    setQuickLoading(true);
    try {
      let newCust;
      if (isOnline) {
        const r = await posAPI.createCustomer({ name: quickName.trim(), mobile: quickMobile.trim() });
        newCust = r.data;
        setCustomers(prev => [newCust, ...prev.filter(c => c.id !== newCust.id)]);
      } else {
        const tempId = `TMP-CUST-${Math.random().toString(36).slice(2, 10).toUpperCase()}`;
        newCust = { id: tempId, name: quickName.trim(), mobile: quickMobile.trim() };
        setCustomers(prev => {
          const updated = [newCust, ...prev];
          putCustomers(updated).catch(() => {});
          return updated;
        });
      }
      setCustomer(newCust);
      setCustomerSearch(newCust.name);
      setQuickAddOpen(false);
      setQuickName('');
      setQuickMobile('');
      toast.success(`Customer ${newCust.name} ${isOnline ? 'added' : 'saved offline'}`);
      if (isMobile) setCameraOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add customer');
    } finally {
      setQuickLoading(false);
    }
  };

  const handleQuickAddProduct = async () => {
    const { name, mrp, cost_price, qty, discount } = qaProductForm;
    if (!name.trim() || !mrp) return;
    setQaProductLoading(true);
    try {
      const cartProduct = {
        catalog_id:       null,
        name:             name.trim(),
        mrp:    parseFloat(mrp),
        gst_rate:         0,
        stock:            parseFloat(qty) || 1,
        barcode:          qaProductBarcode || undefined,
        default_discount: parseFloat(discount) || 0,
      };

      if (isOnline) {
        const today = new Date().toISOString().split('T')[0];
        const res = await purchasesAPI.create({
          purchase_date: today,
          items: [{
            product_name:     name.trim(),
            barcode:          qaProductBarcode || undefined,
            qty:              parseFloat(qty) || 1,
            cost_price:       parseFloat(cost_price) || 0,
            mrp:    parseFloat(mrp),
            default_discount: parseFloat(discount) || undefined,
          }],
        });
        const saved = res.data?.items?.[0];
        if (!saved?.catalog_id) { toast.error('Failed to register product'); return; }
        cartProduct.catalog_id = saved.catalog_id;
        setProducts(prev => [...prev, { ...cartProduct }]);
        putProducts([{ ...cartProduct }]).catch(() => {});
      } else {
        const tempId = `TMP-CAT-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
        cartProduct.catalog_id = tempId;
        setProducts(prev => {
          const updated = [...prev, { ...cartProduct }];
          putProducts(updated).catch(() => {});
          return updated;
        });
        setTempProducts(prev => [...prev, {
          temp_id:          tempId,
          product_name:     name.trim(),
          barcode:          qaProductBarcode || undefined,
          mrp:    parseFloat(mrp),
          cost_price:       parseFloat(cost_price) || 0,
          qty:              parseFloat(qty) || 1,
          default_discount: parseFloat(discount) || 0,
        }]);
      }

      addToCart(cartProduct, { fromBarcode: true });
      toast.success(`${name.trim()} ${isOnline ? 'added and stocked' : 'queued — will sync when online'}`);
      setQaProductOpen(false);
      setQaProductForm({ name: '', mrp: '', cost_price: '', qty: '1', discount: '' });
      setQaProductBarcode('');
      setQaDiscountMode('percent');
      setQaDiscountRs('');
      if (isMobile) setCameraOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add product');
    } finally {
      setQaProductLoading(false);
    }
  };

  // ── Order import helpers ──────────────────────────────────────────────────

  const _scoreMatch = (query, candidate) => {
    const norm = s => s.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
    const q = norm(query);
    const c = norm(candidate);
    if (!q || !c) return 0;
    if (q === c) return 1;
    if (c.startsWith(q) || q.startsWith(c)) return 0.88;
    if (c.includes(q) || q.includes(c)) return 0.72;
    const qWords = q.split(' ').filter(w => w.length > 1);
    const cWords = c.split(' ').filter(w => w.length > 1);
    const cSet   = new Set(cWords);
    if (!qWords.length) return 0;
    let overlap = 0;
    for (const w of qWords) {
      if (cSet.has(w)) { overlap += 1; continue; }
      for (const cw of cSet) {
        if (cw.startsWith(w) || w.startsWith(cw)) { overlap += 0.6; break; }
      }
    }
    return overlap / Math.max(qWords.length, cWords.length);
  };

  const _fuzzyMatchProducts = (parsedItems, productList) =>
    parsedItems.map(item => {
      let bestMatch = null;
      let bestScore = 0;
      for (const p of productList) {
        const score = _scoreMatch(item.product_name, p.name || p.product_name || '');
        if (score > bestScore) { bestScore = score; bestMatch = p; }
      }
      const confidence = bestScore >= 0.85 ? 'high'
        : bestScore >= 0.55 ? 'medium'
        : bestScore >= 0.25 ? 'low'
        : 'not_found';
      return {
        parsed_name: item.product_name,
        qty:         item.qty,
        match:       bestScore >= 0.25 ? bestMatch : null,
        score:       bestScore,
        confidence,
        selected:    bestScore >= 0.25,
      };
    });

  const handleParseOrder = async () => {
    if (!orderImportFile && !orderImportText.trim()) return;
    setOrderImportLoading(true);
    try {
      const fd = new FormData();
      if (orderImportFile) fd.append('file', orderImportFile);
      else fd.append('text', orderImportText.trim());
      const resp = await posAPI.parseOrder(fd);
      const parsed = resp.data;
      setParsedOrder(parsed);
      setMatchedOrder(_fuzzyMatchProducts(parsed.items || [], products));
      setOrderImportStep(2);
    } catch {
      toast.error('Failed to parse document — please try again');
    } finally {
      setOrderImportLoading(false);
    }
  };

  const handleAddImportedToCart = () => {
    const selected = matchedOrder.filter(r => r.selected && r.match);
    if (!selected.length) { toast.warning('No matched items selected'); return; }
    setCart(prev => {
      let next = [...prev];
      for (const row of selected) {
        const p    = row.match;
        const qty  = row.qty;
        const price = p.mrp ?? 0;
        const gst   = p.gst_rate ?? 0;
        const disc  = p.default_discount ?? 0;
        const unitPrice  = calcUnitCost(price, disc, 'percent');
        const item_total = Math.round(qty * unitPrice * 100) / 100;
        const existing   = next.findIndex(c => c.catalog_id === p.catalog_id);
        if (existing >= 0) {
          next = next.map((c, i) => {
            if (i !== existing) return c;
            const newQty   = c.qty + qty;
            const newTotal = Math.round(newQty * c.unit_price * 100) / 100;
            return { ...c, qty: newQty, item_total: newTotal, gst_amount: calcGst(newTotal, c.gst_rate) };
          });
        } else {
          next.push({
            catalog_id:   p.catalog_id,
            product_name: p.name || p.product_name,
            qty,
            mrp:          price,
            discount:     disc,
            discount_type: 'percent',
            unit_price:   unitPrice,
            gst_rate:     gst,
            item_total,
            gst_amount:   calcGst(item_total, gst),
          });
        }
      }
      return next;
    });
    toast.success(`${selected.length} item${selected.length !== 1 ? 's' : ''} added to cart`);
    setOrderImportOpen(false);
    setOrderImportStep(1);
    setOrderImportFile(null);
    setOrderImportText('');
    setParsedOrder(null);
    setMatchedOrder([]);
  };

  const closeOrderImport = () => {
    setOrderImportOpen(false);
    setOrderImportStep(1);
    setOrderImportFile(null);
    setOrderImportText('');
    setOrderImportDragging(false);
    setParsedOrder(null);
    setMatchedOrder([]);
  };

  // ── Reactive ghost guard ──────────────────────────────────────────────────
  // Re-runs the ghost check whenever the products list is refreshed from the
  // API. Catches stale catalog_ids that slipped in during the IndexedDB fast
  // path before the user ever opens the payment modal.
  useEffect(() => {
    if (usingCachedData || loadingProducts || products.length === 0) return;
    const productIds = new Set(products.map(p => p.catalog_id));
    // Sanitise inactive slots in the ref
    slotsRef.current.forEach((slot, i) => {
      if (i === activeSlot) return; // active slot handled below via setCart
      slot.cart = slot.cart.filter(item => productIds.has(item.catalog_id));
    });
    setCart(prev => {
      const valid = prev.filter(item => productIds.has(item.catalog_id));
      if (valid.length === prev.length) return prev;
      const names = prev
        .filter(item => !productIds.has(item.catalog_id))
        .map(i => i.product_name).join(', ');
      toast.warning(`Removed from cart (no longer in catalog): ${names}`);
      return valid;
    });
  }, [products, usingCachedData, loadingProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Split payment helpers ─────────────────────────────────────────────────

  const splitTotal = useMemo(() =>
    Object.values(splitAmounts).reduce((s, v) => s + (parseFloat(v) || 0), 0),
  [splitAmounts]);

  const splitRemaining = Math.round((cartTotals.total - splitTotal) * 100) / 100;

  const buildSplitPayments = () =>
    Object.entries(splitAmounts)
      .filter(([, v]) => parseFloat(v) > 0)
      .map(([mode, amount]) => ({ mode, amount: parseFloat(amount) }));

  // ── Checkout ──────────────────────────────────────────────────────────────

  const handleCheckout = async (overrideMode = null) => {
    if (cart.length === 0) return;

    // Supplier gate: every bill must go to a Kiranaflow-connected retailer.
    if (user?.role === 'supplier') {
      if (!customer) {
        toast.error('Please select a retailer before creating an invoice.');
        customerInputRef.current?.focus();
        return;
      }
      if (!customer.kiranaflow_store_id) {
        setRetailerGateError(true);
        return;
      }
    }

    if (splitMode && Math.abs(splitRemaining) > 0.01) {
      toast.error(`Split amounts must sum to ${fmt(cartTotals.total)}`);
      return;
    }

    // Guard: catch any cart items whose catalog_id no longer exists in the
    // current product list (ghost entries from catalog merges or deletions).
    // Skip this check in amendment mode — the cart was pre-filled from the
    // original bill's catalog_ids which are still valid on the backend even if
    // they were later removed from the active catalog.
    // Items actually submitted — defaults to `cart`, narrowed below if ghost
    // items are filtered out, so the payload/receipt never diverge from what
    // the user is shown.
    let cartForSubmit = cart;
    if (!amendSale) {
      const ghostItems = cart.filter(item =>
        !products.some(p => p.catalog_id === item.catalog_id)
      );
      if (ghostItems.length > 0) {
        const names = ghostItems.map(i => i.product_name).join(', ');
        toast.warning(`Removed from cart (no longer in catalog): ${names}`);
        cartForSubmit = cart.filter(item => products.some(p => p.catalog_id === item.catalog_id));
        setCart(cartForSubmit);
        if (cartForSubmit.length === 0) return;
      }
    }

    const collectAmt = parseFloat(khataCollectAmount) || 0;
    const snapKhataBalance = customer?.khata_balance ?? 0;

    const payload = {
      items: cartForSubmit.map(item => ({
        catalog_id:    item.catalog_id,
        product_name:  item.product_name,
        qty:           item.qty,
        unit_price:    item.unit_price,
        mrp:           item.mrp,
        gst_rate:      item.gst_rate,
        hsn_code:      item.hsn_code ?? '',
        discount:      item.discount ?? 0,
        discount_type: item.discount_type ?? 'percent',
      })),
      payment_mode:          overrideMode ?? (splitMode ? 'split' : selectedMode),
      split_payments:        splitMode ? buildSplitPayments() : null,
      customer_name:         customer?.name         || null,
      customer_id:           customer?.id           || null,
      customer_mobile:       customer?.mobile       || null,
      customer_gst_number:   customer?.gst_number   || null,
      customer_fssai_number: customer?.fssai_number || null,
      customer_address:      customer?.address      || null,
      gst_inclusive:         true,
      original_bill_number:  amendSale?.bill_number || null,
      overall_discount:      cartTotals.discount_amt,
      overall_discount_type: overallDiscountType,
      round_off:             cartTotals.round_off,
      khata_cleared:         collectAmt,
      khata_remaining:       collectAmt > 0 ? Math.max(0, (customer?.khata_balance ?? 0) - collectAmt) : 0,
    };

    const khataAdded = payload.payment_mode === 'khata'
      ? cartTotals.rounded_total
      : (payload.split_payments?.reduce((s, p) => p.mode === 'khata' ? s + p.amount : s, 0) ?? 0);
    const khataExtras = { existing_khata_balance: snapKhataBalance, khata_added: khataAdded };

    if (!isOnline) {
      const temp_bill_number = generateTempBillNumber();
      // Collect any temp products referenced by items in this sale
      const cartTempProducts = tempProducts.filter(tp =>
        cartForSubmit.some(item => item.catalog_id === tp.temp_id)
      );
      // Detect if the selected customer is a temp (offline-created) customer
      const tempCust = customer?.id?.startsWith('TMP-CUST-')
        ? { temp_id: customer.id, name: customer.name, mobile: customer.mobile }
        : undefined;
      await enqueueOfflineSale({
        temp_bill_number, payload,
        created_at: new Date().toISOString(),
        status: 'pending', real_bill_number: null, error: null,
        temp_products: cartTempProducts.length ? cartTempProducts : undefined,
        temp_customer: tempCust,
      });
      // Clear used temp products so they aren't re-included in the next sale
      if (cartTempProducts.length) {
        setTempProducts(prev => prev.filter(tp =>
          !cartTempProducts.some(ct => ct.temp_id === tp.temp_id)
        ));
      }
      await refreshPendingCount();
      setReceiptSale(buildFakeReceiptSale(temp_bill_number, payload, cartForSubmit, cartTotals, khataExtras));
      slotsRef.current[activeSlot] = { ...EMPTY_SLOT };
      setCart([]); setCustomer(null); setCustomerSearch('');
      setOverallDiscount(''); setOverallDiscountType('percent');
      setPaymentOpen(false); setSplitMode(false);
      setSplitAmounts({ cash: '', card: '', upi: '', khata: '' });
      setKhataCollectAmount('');
      refocusSearch(); // ready for the next scan even while this sale's receipt is still shown
      if (amendSale) navigate(user?.role === 'supplier' ? '/supplier/billing' : '/pos', { replace: true, state: {} });
      toast.info(`Saved offline — Bill# ${temp_bill_number}`);
      return;
    }

    // Snapshot data needed for offline fallback in case network drops mid-request.
    const cartTempProductsSnap = tempProducts.filter(tp =>
      cartForSubmit.some(item => item.catalog_id === tp.temp_id)
    );
    const tempCustSnap = customer?.id?.startsWith('TMP-CUST-')
      ? { temp_id: customer.id, name: customer.name, mobile: customer.mobile }
      : undefined;

    // Show optimistic receipt immediately — bill number fills in when backend responds.
    setReceiptSale(buildFakeReceiptSale('PENDING', payload, cartForSubmit, cartTotals, khataExtras));
    setPaymentOpen(false); setSplitMode(false);
    setSplitAmounts({ cash: '', card: '', upi: '', khata: '' });
    setKhataCollectAmount('');

    setCheckoutLoading(true);
    try {
      const r = await posAPI.createSale(payload);
      const sale = r.data.sale;
      if (!sale.customer_name && customer?.name) sale.customer_name = customer.name;

      // Capture khata context before clearing customer state
      const snapCustId   = customer?.id;
      const payloadKhata = payload.payment_mode === 'khata'
        ? cartTotals.total
        : (payload.split_payments?.reduce((s, p) => p.mode === 'khata' ? s + p.amount : s, 0) ?? 0);

      // Attach khata snapshot so ReceiptModal can show the full 3-row block.
      sale.existing_khata_balance = snapKhataBalance;
      sale.khata_added = payloadKhata;

      // Clear cart after confirmed success.
      slotsRef.current[activeSlot] = { ...EMPTY_SLOT };
      setCart([]); setCustomer(null); setCustomerSearch('');
      setOverallDiscount(''); setOverallDiscountType('percent');
      setReceiptSale(sale); // Replace optimistic receipt with real sale.
      successHaptic();
      refocusSearch(); // ready for the next scan even while this sale's receipt is still shown
      if (amendSale) navigate(user?.role === 'supplier' ? '/supplier/billing' : '/pos', { replace: true, state: {} });
      if (r.data.stock_warnings?.length > 0) r.data.stock_warnings.forEach(w => toast.warning(w));

      // Update local customers state with authoritative khata balance — never delay receipt.
      if (collectAmt > 0 && snapCustId) {
        khataAPI.collect({ customer_id: snapCustId, amount: collectAmt, note: 'Collected at POS', bill_number: sale.bill_number })
          .then(res => {
            toast.success(`₹${collectAmt.toLocaleString('en-IN')} khata collected`);
            const newBal = res.data?.new_balance ?? 0;
            setCustomers(prev => {
              const updated = prev.map(c => c.id === snapCustId ? { ...c, khata_balance: newBal } : c);
              putCustomers(updated.filter(c => c.id === snapCustId)).catch(() => {});
              return updated;
            });
          })
          .catch(() => toast.warning('Sale saved but khata collection failed — collect manually from Customer Insights'));
      } else if (snapCustId && (payloadKhata > 0 || amendSale)) {
        // Sale added khata OR amendment — fetch authoritative balance (background, no toast)
        customersAPI.get(snapCustId)
          .then(res => {
            const fresh = res.data;
            setCustomers(prev => {
              const updated = prev.map(c => c.id === snapCustId ? fresh : c);
              putCustomers([fresh]).catch(() => {});
              return updated;
            });
          })
          .catch(() => {});
      }
    } catch (err) {
      if (!err.response) {
        // Network error mid-checkout — save to offline queue so the sale isn't lost.
        const temp_bill_number = generateTempBillNumber();
        await enqueueOfflineSale({
          temp_bill_number, payload,
          created_at: new Date().toISOString(),
          status: 'pending', real_bill_number: null, error: null,
          temp_products: cartTempProductsSnap.length ? cartTempProductsSnap : undefined,
          temp_customer: tempCustSnap,
        });
        if (cartTempProductsSnap.length) {
          setTempProducts(prev => prev.filter(tp =>
            !cartTempProductsSnap.some(ct => ct.temp_id === tp.temp_id)
          ));
        }
        await refreshPendingCount();
        setReceiptSale(buildFakeReceiptSale(temp_bill_number, payload, cartForSubmit, cartTotals, khataExtras));
        slotsRef.current[activeSlot] = { ...EMPTY_SLOT };
        setCart([]); setCustomer(null); setCustomerSearch('');
        setOverallDiscount(''); setOverallDiscountType('percent');
        refocusSearch(); // ready for the next scan even while this sale's receipt is still shown
        if (amendSale) navigate(user?.role === 'supplier' ? '/supplier/billing' : '/pos', { replace: true, state: {} });
        toast.info(`No internet — Bill# ${temp_bill_number} saved offline, syncs when back online.`);
      } else {
        setReceiptSale(null); // Server error — revert receipt; cart intact for retry.
        errorHaptic();
        toast.error(err?.response?.data?.detail || 'Checkout failed');
        refocusSearch(); // cart is intact for retry — search should already be ready for it
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Click-to-refocus: clicking any non-interactive area of the page returns focus
  // to the search bar so barcode scanner input is always captured. No-ops on
  // mobile (forcing focus pops the keyboard) and while any modal/receipt is open.
  const handlePageClick = (e) => {
    if (isMobile || anyModalOpen) return;
    if (document.activeElement === searchRef.current) return;
    if (window.getSelection()?.toString()) return; // mid text-selection drag
    if (e.target.closest('input, select, textarea, button, a, [role="button"], [contenteditable="true"]')) return;
    searchRef.current?.focus();
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-background" onClick={handlePageClick}>

      {/* ── Header ── */}
      {isMobile ? (
        <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card gap-3">
          <h1 className="text-base font-semibold text-foreground whitespace-nowrap shrink-0">Point of Sale</h1>
          {!bannerDismissed && (
            <SubscriptionWarningBanner
              validUntil={storeProfile?.membership_valid_until}
              compact
              onDismiss={() => { setBannerDismissed(true); sessionStorage.setItem('pos_banner_dismissed', '1'); }}
            />
          )}
          <div className="inline-flex items-center gap-0.5 bg-muted rounded-lg p-0.5 shrink-0">
            <button className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all bg-background text-foreground shadow-sm whitespace-nowrap">
              <ShoppingCart className="w-3 h-3 shrink-0" />
              POS
            </button>
            <button
              onClick={() => navigate(user?.role === 'supplier' ? '/supplier/billing?view=history' : '/pos?view=history')}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
            >
              <History className="w-3 h-3 shrink-0" />
              Sales History
            </button>
            {user?.role === 'supplier' && (
              <button
                onClick={() => navigate('/supplier/billing?view=invoices')}
                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground whitespace-nowrap"
              >
                <Truck className="w-3 h-3 shrink-0" />
                Invoices
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center px-6 py-2.5 border-b border-border bg-card gap-8">
          {/* Customer slot tabs — wide rectangular, left-anchored after title */}
          {!amendSale && (
            <div className="flex flex-1 items-center gap-1.5">
              {[0, 1, 2].map(i => {
                const slotItemCount = i === activeSlot ? cart.length : slotsRef.current[i].cart.length;
                const isActive = i === activeSlot;
                return (
                  <button
                    key={i}
                    tabIndex={-1}
                    onClick={() => switchSlot(i)}
                    className={`flex flex-1 items-center gap-2 py-2.5 px-6 rounded-md text-xs font-medium transition-colors justify-center
                      ${isActive
                        ? 'bg-emerald-600 text-white shadow-sm'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                  >
                    Customer {i + 1}
                    {slotItemCount > 0 && (
                      <span className={`text-[10px] px-1.5 py-0 rounded-full font-bold leading-none ${isActive ? 'bg-white/30 text-white' : 'bg-background text-foreground'}`}>
                        {slotItemCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex items-center gap-3">
            {!bannerDismissed && (
              <SubscriptionWarningBanner
                validUntil={storeProfile?.membership_valid_until}
                compact
                onDismiss={() => { setBannerDismissed(true); sessionStorage.setItem('pos_banner_dismissed', '1'); }}
              />
            )}
            <div className="inline-flex items-center gap-1 bg-muted rounded-lg p-1">
              <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all bg-background text-foreground shadow-sm">
                <ShoppingCart className="w-3.5 h-3.5" />
                Point of Sale
              </button>
              <button
                onClick={() => navigate(user?.role === 'supplier' ? '/supplier/billing?view=history' : '/pos?view=history')}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground"
              >
                <History className="w-3.5 h-3.5" />
                Sales History
              </button>
              {user?.role === 'supplier' && (
                <button
                  onClick={() => navigate('/supplier/billing?view=invoices')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all text-muted-foreground hover:text-foreground"
                >
                  <Truck className="w-3.5 h-3.5" />
                  Sent Invoices
                </button>
              )}
            </div>
          </div>
        </div>
      )}


      {/* ── Inline camera strip (mobile only) — sits above search bar ── */}
      {cameraOpen && isMobile && (
        <CameraScanner
          preWarmStream={preWarmStreamRef.current}
          continuous
          inline
          lastDetectedName={lastScannedName}
          onDetected={handleBarcodeDetected}
          onClose={() => setCameraOpen(false)}
        />
      )}

      {/* ── Search bar ── */}
      <div className="px-4 pt-2 pb-1.5 bg-card border-b border-border">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              ref={searchRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => { if (isMobile) setCameraOpen(false); if (searchQuery.trim()) setShowDropdown(true); }}
              onBlur={() => setShowDropdown(false)}
              placeholder="Search by name or scan barcode..."
              className={`w-full pl-9 py-2.5 rounded-lg border border-border focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm ${isMobile ? 'pr-14' : 'pr-4'}`}
            />
            {isMobile && (
              <button
                type="button"
                onPointerDown={(e) => {
                  e.preventDefault(); // prevent input blur → no layout shift on Android
                  const willOpen = !cameraOpen;
                  setCameraOpen(willOpen);
                  if (willOpen) requestAnimationFrame(() => searchRef.current?.blur());
                }}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-2.5 rounded hover:bg-muted transition-colors"
                title="Toggle camera"
              >
                <Camera className={`h-4 w-4 ${cameraOpen ? 'text-primary' : 'text-muted-foreground'}`} />
              </button>
            )}
            {showDropdown && searchQuery.trim() && (
              <div ref={dropdownRef} className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
                {searchResults.map((p, i) => (
                  <button
                    key={p.catalog_id}
                    onMouseDown={(e) => { e.preventDefault(); addToCart(p, { fromMouse: true }); }}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-sm ${i === selectedIdx ? 'bg-primary/10' : 'hover:bg-muted/60'}`}
                  >
                    <div className="text-left">
                      <div className="font-medium">{p.name || p.product_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {p.barcode && <span className="mr-2">{p.barcode}</span>}
                        {p.category && <span>{p.category}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <div className="font-semibold text-emerald-700">{fmt(p.mrp ?? 0)}</div>
                      <div className="text-xs text-muted-foreground">Stock: {p.stock ?? 0}</div>
                    </div>
                  </button>
                ))}
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    openQaModal(searchQuery.trim());
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-primary ${searchResults.length === selectedIdx ? 'bg-primary/10' : 'hover:bg-primary/5'} ${searchResults.length > 0 ? 'border-t border-border' : ''}`}
                >
                  <PackagePlus className="h-4 w-4 shrink-0" />
                  <span>Quick add &ldquo;{searchQuery}&rdquo;</span>
                </button>
              </div>
            )}
          </div>
          <button
            tabIndex={0}
            onClick={() => openQaModal(searchQuery.trim())}
            className={`${isMobile ? 'p-3' : 'p-2'} rounded-lg border border-border hover:bg-muted/60 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary`}
            title="Quick add product (Alt+P)"
          >
            <PackagePlus className={isMobile ? 'h-6 w-6' : 'h-4 w-4'} />
          </button>
          <button
            tabIndex={-1}
            onClick={() => setOrderImportOpen(true)}
            className={`${isMobile ? 'p-3' : 'p-2'} rounded-lg border border-border hover:bg-violet-50 hover:border-violet-300 text-muted-foreground hover:text-violet-600 focus:outline-none focus:ring-1 focus:ring-violet-400 transition-colors`}
            title="Import order from document / photo / text"
          >
            <FileText className={isMobile ? 'h-6 w-6' : 'h-4 w-4'} />
          </button>
          {!isMobile && (
            <button
              tabIndex={-1}
              onClick={() => setShortcutsOpen(true)}
              className="p-2 rounded-lg border border-border hover:bg-muted/60 text-muted-foreground"
              title="Keyboard shortcuts"
            >
              <Keyboard className="h-4 w-4" />
            </button>
          )}
        </div>
        {catalogRefreshing && (
          <div className="flex items-center gap-1.5 mt-1">
            <svg className="animate-spin h-3 w-3 text-emerald-500" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-xs text-emerald-600 font-medium">Syncing catalogue…</span>
          </div>
        )}
      </div>

      {/* ── Cart ── */}
      <div ref={cartScrollRef} className="flex-1 overflow-auto scrollbar-wide">
        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-20">
            <ShoppingCart className="h-16 w-16 mb-4 opacity-20" />
            <p className="text-lg font-medium">Cart is empty</p>
            <p className="text-sm mt-1">Search above or scan a barcode to add items</p>
          </div>
        ) : isMobile ? (
          /* ── Mobile card list (no horizontal scroll) ── */
          <div className="divide-y divide-border">
            {cart.map((item, idx) => (
              <div key={item.catalog_id} className="px-3 py-2">
                {/* Row 1: name · total · delete */}
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <span className="font-medium text-sm flex-1 min-w-0 truncate">{item.product_name}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="font-semibold text-emerald-700 text-sm">{fmtN(item.item_total)}</span>
                    <button tabIndex={-1} onClick={() => removeItem(item.catalog_id)} className="p-2 -m-2 text-red-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {/* Row 2: qty controls · price · discount */}
                <div className="flex items-center gap-2">
                  {/* Qty */}
                  <div className="flex items-center gap-1 shrink-0">
                    <button tabIndex={-1} onClick={() => item.qty > 1 ? updateQty(idx, item.qty - 1) : removeItem(item.catalog_id)}
                      className="h-8 w-8 rounded border border-border flex items-center justify-center hover:bg-muted/60 text-muted-foreground">
                      <Minus className="h-3 w-3" />
                    </button>
                    <QtyInput
                      inputRef={el => qtyInputRefs.current[idx] = el}
                      value={item.qty}
                      onCommit={qty => updateQty(idx, qty)}
                      onRemove={() => removeItem(item.catalog_id)}
                      className="w-14"
                      onFocus={e => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                      onEscape={() => searchRef.current?.focus()}
                      onKeyDown={e => {
                        if (e.key === 'Enter') searchRef.current?.focus();
                        if (e.key === 'Delete' && e.target.value === '') { e.preventDefault(); removeItem(item.catalog_id); }
                      }}
                    />
                    <button tabIndex={-1} onClick={() => updateQty(idx, item.qty + 1)}
                      className="h-8 w-8 rounded border border-border flex items-center justify-center hover:bg-muted/60 text-muted-foreground">
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  {/* MRP */}
                  <div className="relative flex-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">₹</span>
                    <PriceInput
                      ref={el => spInputRefs.current[idx] = el}
                      tabIndex={-1}
                      min="0.01"
                      step="0.01"
                      minValue={0.01}
                      value={item.mrp}
                      onCommit={val => updateSellingPrice(idx, val)}
                      onFocus={e => {
                        e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        mrpOriginalValueRef.current = item.mrp;
                        mrpSaveModeIdxRef.current = idx;
                        setMrpSaveModeIdx(idx);
                      }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchRef.current?.focus(); } }}
                      onEscape={() => {
                        mrpSaveModeIdxRef.current = null;
                        setMrpSaveModeIdx(null);
                        mrpOriginalValueRef.current = null;
                        searchRef.current?.focus();
                      }}
                      onBlurExtra={async (e) => {
                        if (mrpSaveModeIdxRef.current !== idx) return;
                        mrpSaveModeIdxRef.current = null;
                        setMrpSaveModeIdx(null);
                        const newMrp = parseFloat(e.target.value) || 0;
                        const originalMrp = mrpOriginalValueRef.current;
                        mrpOriginalValueRef.current = null;
                        if (!item.catalog_id || item.catalog_id.startsWith('TMP-')) return;
                        if (originalMrp !== null && Math.abs(newMrp - originalMrp) < 0.001) return;
                        try {
                          await catalogAPI.update(item.catalog_id, { mrp: newMrp });
                          toast.success('MRP saved to stock master');
                          // Instantly reflect the new MRP locally and notify other devices
                          setProducts(prev => {
                            const next = prev.map(p =>
                              p.catalog_id === item.catalog_id ? { ...p, mrp: newMrp } : p
                            );
                            const updated = next.find(p => p.catalog_id === item.catalog_id);
                            if (updated) putProducts([updated]).catch(() => {});
                            return next;
                          });
                          const syntheticProds = [{ catalog_id: item.catalog_id, mrp: newMrp }];
                          slotsRef.current.forEach(slot => {
                            slot.cart = slot.cart.map(ci => reconcileCartItem(ci, syntheticProds));
                          });
                          catalogChannelRef.current?.send({
                            type: 'broadcast', event: 'catalog_changed',
                            payload: { catalog_id: item.catalog_id, mrp: newMrp },
                          });
                        } catch {
                          toast.error('Failed to save MRP to stock master');
                        }
                      }}
                      className={`w-full pl-5 pr-2 py-1.5 rounded text-sm text-right focus:outline-none focus:ring-1 bg-background border ${
                        mrpSaveModeIdx === idx
                          ? 'border-amber-400 ring-1 ring-amber-400 focus:ring-amber-400'
                          : 'border-border focus:ring-primary'
                      }`}
                    />
                  </div>
                  {/* Unit Cost */}
                  <div className="relative shrink-0">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">₹</span>
                    <PriceInput
                      ref={el => upInputRefs.current[idx] = el}
                      tabIndex={-1}
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onCommit={val => updateUnitPrice(idx, val)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchRef.current?.focus(); } }}
                      onEscape={() => searchRef.current?.focus()}
                      className={`w-20 pl-5 pr-1 py-1.5 border border-border rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-primary bg-background ${
                        item.discount > 0 ? 'text-emerald-700' : 'text-foreground'
                      }`}
                    />
                  </div>
                  {/* Discount */}
                  <div className="flex items-center gap-1 shrink-0">
                    <input
                      tabIndex={-1}
                      type="number"
                      value={item.discount || ''}
                      step="0.01"
                      placeholder="0"
                      onFocus={() => { discountOriginalRef.current = item.discount; }}
                      onChange={e => updateDiscount(idx, e.target.value)}
                      onKeyDown={e => {
                        blockNonNumeric(e);
                        if (e.key === 'Escape') {
                          e.preventDefault();
                          updateDiscount(idx, discountOriginalRef.current || '');
                          searchRef.current?.focus();
                          return;
                        }
                        if (e.key === 'Enter') { e.preventDefault(); searchRef.current?.focus(); }
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                          e.preventDefault();
                          const n = parseFloat(item.discount) || 0;
                          const next = e.key === 'ArrowUp'
                            ? (Number.isInteger(n) ? n + 1 : Math.ceil(n))
                            : (Number.isInteger(n) ? Math.max(0, n - 1) : Math.floor(n));
                          updateDiscount(idx, String(next));
                          const el = e.target;
                          requestAnimationFrame(() => el.select());
                        }
                      }}
                      className="w-14 px-2 py-1.5 border border-border rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <button tabIndex={-1} onClick={() => toggleDiscountType(idx)}
                      title={item.discount_type === 'amount' ? 'Switch to percent' : 'Switch to rupees'}
                      className="h-8 w-8 border border-border rounded text-xs font-bold hover:bg-muted/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
                      {item.discount_type === 'amount' ? '₹' : '%'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* ── Desktop table ── */
          <table className="w-full min-w-[860px]">
            <thead className="border-b border-border bg-card sticky top-0 z-10">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Product</th>
                <th className="text-center px-3 py-3 text-sm font-medium text-muted-foreground w-28">Quantity</th>
                <th className="text-center px-3 py-3 text-sm font-medium text-muted-foreground w-28">MRP</th>
                <th className="text-center px-3 py-3 text-sm font-medium text-muted-foreground w-28">Discount</th>
                <th className="text-center px-3 py-3 text-sm font-medium text-muted-foreground w-28">Unit Cost</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground w-28">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {cart.map((item, idx) => (
                <tr key={item.catalog_id} className="hover:bg-muted/20">

                  {/* Product */}
                  <td className="px-4 py-0.5">
                    <div className="font-medium text-[13px]">{item.product_name}</div>
                  </td>

                  {/* Quantity */}
                  <td className="px-3 py-0.5">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        tabIndex={-1}
                        onClick={() => { if (item.qty > 1) updateQty(idx, item.qty - 1); else removeItem(item.catalog_id); refocusSearch(); }}
                        className="h-5 w-5 rounded border border-border flex items-center justify-center hover:bg-muted/60 text-muted-foreground"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <QtyInput
                        inputRef={el => qtyInputRefs.current[idx] = el}
                        value={item.qty}
                        onCommit={qty => updateQty(idx, qty)}
                        onRemove={() => removeItem(item.catalog_id)}
                        onEscape={() => searchRef.current?.focus()}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            searchRef.current?.focus();
                          }
                          if (e.key === 'Tab' && !e.shiftKey) {
                            e.preventDefault();
                            spInputRefs.current[idx]?.focus();
                            spInputRefs.current[idx]?.select();
                          }
                          if (e.key === 'Tab' && e.shiftKey) {
                            e.preventDefault();
                            if (idx > 0) {
                              upInputRefs.current[idx - 1]?.focus();
                              upInputRefs.current[idx - 1]?.select();
                            } else {
                              searchRef.current?.focus();
                            }
                          }
                          if (e.key === 'Delete' && e.target.value === '') {
                            e.preventDefault();
                            removeItem(item.catalog_id);
                          }
                        }}
                      />
                      <button
                        tabIndex={-1}
                        onClick={() => { updateQty(idx, item.qty + 1); refocusSearch(); }}
                        className="h-5 w-5 rounded border border-border flex items-center justify-center hover:bg-muted/60 text-muted-foreground"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                  </td>

                  {/* MRP */}
                  <td className="px-3 py-0.5">
                    <div className="flex justify-center">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">₹</span>
                      <PriceInput
                        ref={el => spInputRefs.current[idx] = el}
                        tabIndex={0}
                        min="0.01"
                        step="0.01"
                        minValue={0.01}
                        value={item.mrp}
                        onCommit={val => updateSellingPrice(idx, val)}
                        onFocus={() => {
                          mrpOriginalValueRef.current = item.mrp;
                          mrpSaveModeIdxRef.current = idx;
                          setMrpSaveModeIdx(idx);
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); searchRef.current?.focus(); }
                          if (e.key === 'Tab' && !e.shiftKey) {
                            e.preventDefault();
                            discountInputRefs.current[idx]?.focus();
                            discountInputRefs.current[idx]?.select();
                          }
                          if (e.key === 'Tab' && e.shiftKey) {
                            e.preventDefault();
                            qtyInputRefs.current[idx]?.focus();
                            qtyInputRefs.current[idx]?.select();
                          }
                        }}
                        onEscape={() => {
                          mrpSaveModeIdxRef.current = null;
                          setMrpSaveModeIdx(null);
                          mrpOriginalValueRef.current = null;
                          searchRef.current?.focus();
                        }}
                        onBlurExtra={async (e) => {
                          if (mrpSaveModeIdxRef.current !== idx) return;
                          mrpSaveModeIdxRef.current = null;
                          setMrpSaveModeIdx(null);
                          const newMrp = parseFloat(e.target.value) || 0;
                          const originalMrp = mrpOriginalValueRef.current;
                          mrpOriginalValueRef.current = null;
                          if (!item.catalog_id || item.catalog_id.startsWith('TMP-')) return;
                          if (originalMrp !== null && Math.abs(newMrp - originalMrp) < 0.001) return;
                          try {
                            await catalogAPI.update(item.catalog_id, { mrp: newMrp });
                            toast.success('MRP saved to stock master');
                            // Instantly reflect the new MRP locally and notify other devices
                            setProducts(prev => {
                              const next = prev.map(p =>
                                p.catalog_id === item.catalog_id ? { ...p, mrp: newMrp } : p
                              );
                              const updated = next.find(p => p.catalog_id === item.catalog_id);
                              if (updated) putProducts([updated]).catch(() => {});
                              return next;
                            });
                            const syntheticProds = [{ catalog_id: item.catalog_id, mrp: newMrp }];
                            slotsRef.current.forEach(slot => {
                              slot.cart = slot.cart.map(ci => reconcileCartItem(ci, syntheticProds));
                            });
                            catalogChannelRef.current?.send({
                              type: 'broadcast', event: 'catalog_changed',
                              payload: { catalog_id: item.catalog_id, mrp: newMrp },
                            });
                          } catch {
                            toast.error('Failed to save MRP to stock master');
                          }
                        }}
                        className={`w-20 pl-5 pr-2 py-1 rounded text-[13px] text-right focus:outline-none focus:ring-1 bg-background border ${
                          mrpSaveModeIdx === idx
                            ? 'border-amber-400 ring-1 ring-amber-400 focus:ring-amber-400'
                            : 'border-border focus:ring-primary'
                        }`}
                      />
                    </div>
                    </div>
                  </td>

                  {/* Discount */}
                  <td className="px-3 py-0.5">
                    <div className="flex items-center justify-center">
                      <input
                        ref={el => discountInputRefs.current[idx] = el}
                        tabIndex={0}
                        type="number"
                        value={item.discount || ''}
                        step="0.01"
                        placeholder="0"
                        onFocus={() => { discountOriginalRef.current = item.discount; }}
                        onChange={e => updateDiscount(idx, e.target.value)}
                        onKeyDown={e => {
                          blockNonNumeric(e);
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            updateDiscount(idx, discountOriginalRef.current || '');
                            searchRef.current?.focus();
                            return;
                          }
                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            e.preventDefault();
                            const n = parseFloat(item.discount) || 0;
                            const next = e.key === 'ArrowUp'
                              ? (Number.isInteger(n) ? n + 1 : Math.ceil(n))
                              : (Number.isInteger(n) ? Math.max(0, n - 1) : Math.floor(n));
                            updateDiscount(idx, String(next));
                            const el = e.target;
                            requestAnimationFrame(() => el.select());
                            return;
                          }
                          if (e.key === 'Enter') { e.preventDefault(); searchRef.current?.focus(); }
                          if (e.key === 'Tab' && !e.shiftKey) {
                            e.preventDefault();
                            upInputRefs.current[idx]?.focus();
                            upInputRefs.current[idx]?.select();
                          }
                          if (e.key === 'Tab' && e.shiftKey) {
                            e.preventDefault();
                            spInputRefs.current[idx]?.focus();
                            spInputRefs.current[idx]?.select();
                          }
                        }}
                        className="w-14 px-2 py-1 border border-border rounded-l text-[13px] text-center focus:outline-none focus:ring-1 focus:ring-primary [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <button
                        tabIndex={-1}
                        onClick={() => { toggleDiscountType(idx); refocusSearch(); }}
                        title={item.discount_type === 'amount' ? 'Switch to percent' : 'Switch to rupees'}
                        className="h-[30px] w-[30px] shrink-0 border border-l-0 border-border rounded-r text-xs font-bold hover:bg-muted/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {item.discount_type === 'amount' ? '₹' : '%'}
                      </button>
                    </div>
                  </td>

                  {/* Unit Cost */}
                  <td className="px-3 py-0.5">
                    <div className="flex justify-center">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">₹</span>
                    <PriceInput
                      ref={el => upInputRefs.current[idx] = el}
                      tabIndex={0}
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onCommit={val => updateUnitPrice(idx, val)}
                      onEscape={() => searchRef.current?.focus()}
                      onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); searchRef.current?.focus(); }
                        if (e.key === 'Tab' && !e.shiftKey) {
                          e.preventDefault();
                          trashInputRefs.current[idx]?.focus();
                        }
                        if (e.key === 'Tab' && e.shiftKey) {
                          e.preventDefault();
                          discountInputRefs.current[idx]?.focus();
                          discountInputRefs.current[idx]?.select();
                        }
                      }}
                      className={`w-20 pl-5 pr-1 py-1 border border-border rounded text-[13px] text-right focus:outline-none focus:ring-1 focus:ring-primary bg-background ${
                        item.discount > 0 ? 'text-emerald-700' : 'text-foreground'
                      }`}
                    />
                    </div>
                    </div>
                  </td>

                  {/* Total + Delete */}
                  <td className="px-4 py-0.5 relative">
                    <span className="block text-center font-semibold text-emerald-700 text-[13px]">{fmtN(item.item_total)}</span>
                    <button
                      ref={el => trashInputRefs.current[idx] = el}
                      tabIndex={0}
                      onMouseDown={(e) => { e.preventDefault(); removeItem(item.catalog_id); refocusSearch(); }}
                      onClick={() => { removeItem(item.catalog_id); refocusSearch(); }}
                      onKeyDown={e => {
                        if (e.key === 'Tab' && !e.shiftKey) {
                          e.preventDefault();
                          if (idx < cart.length - 1) {
                            qtyInputRefs.current[idx + 1]?.focus();
                            qtyInputRefs.current[idx + 1]?.select();
                          } else {
                            searchRef.current?.focus();
                          }
                        }
                        if (e.key === 'Tab' && e.shiftKey) {
                          e.preventDefault();
                          upInputRefs.current[idx]?.focus();
                          upInputRefs.current[idx]?.select();
                        }
                      }}
                      className="absolute right-0 top-1/2 -translate-y-1/2 p-2.5 text-red-400 hover:text-red-600 transition-colors focus:outline-none focus:ring-1 focus:ring-primary rounded"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div ref={cartEndRef} />
      </div>

      {/* ── Bottom bar ── */}
      {cart.length > 0 && (
        <div className={`border-t border-border bg-card py-1.5 space-y-1.5 ${isMobile ? 'px-4 safe-area-pb' : 'px-4'}`}>
          {/* Discount row — mobile: compact / desktop: column-aligned */}
          {isMobile ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground shrink-0">Disc:</span>
              <div className="flex items-center gap-1">
                <input
                  ref={overallDiscountRef}
                  type="number"
                  min="0"
                  step="0.01"
                  value={overallDiscount}
                  onChange={e => setOverallDiscount(e.target.value)}
                  onKeyDown={e => {
                    blockNonNumeric(e);
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      e.preventDefault();
                      const n = parseFloat(overallDiscount) || 0;
                      const next = e.key === 'ArrowUp'
                        ? (Number.isInteger(n) ? n + 1 : Math.ceil(n))
                        : (Number.isInteger(n) ? Math.max(0, n - 1) : Math.floor(n));
                      setOverallDiscount(String(next));
                    }
                  }}
                  placeholder="0"
                  className="w-16 px-2 py-1 border border-border rounded-lg text-sm text-right focus:outline-none focus:ring-1 focus:ring-primary"
                  tabIndex={-1}
                />
                <button
                  tabIndex={-1}
                  onClick={() => setOverallDiscountType(t => t === 'amount' ? 'percent' : 'amount')}
                  className="h-8 w-8 border border-border rounded-lg text-xs font-bold hover:bg-muted/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                  title={overallDiscountType === 'amount' ? 'Switch to percent' : 'Switch to rupees'}
                >
                  {overallDiscountType === 'amount' ? '₹' : '%'}
                </button>
              </div>
              {(cartTotals.discount_amt > 0 || cartTotals.line_item_discount_amt > 0) && (
                <span className="text-xs text-emerald-700 font-medium">−{fmt(cartTotals.line_item_discount_amt + cartTotals.discount_amt)}</span>
              )}
              {cartTotals.round_off !== 0 && (
                <span className="text-xs text-slate-500">
                  Rnd {cartTotals.round_off > 0 ? '+' : ''}{fmt(cartTotals.round_off)}
                </span>
              )}
              <div className="flex-1" />
              <div className="flex items-center gap-3 shrink-0 text-muted-foreground">
                <div className="flex items-center gap-1">
                  <ShoppingCart className="w-4 h-4" />
                  <span className="text-sm font-bold text-foreground leading-none">{cart.length}</span>
                  <span className="text-xs">SKUs</span>
                </div>
                <div className="flex items-center gap-1">
                  <Package className="w-4 h-4" />
                  <span className="text-sm font-bold text-foreground leading-none">{cartTotals.total_qty}</span>
                  <span className="text-xs">items</span>
                </div>
              </div>
            </div>
          ) : (
            /* Desktop: counters (left) + overall discount (right) in one row */
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-3 text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <ShoppingCart className="w-4 h-4" />
                  <span className="text-base font-bold text-foreground leading-none">{cart.length}</span>
                  <span className="text-xs">SKUs</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Package className="w-4 h-4" />
                  <span className="text-base font-bold text-foreground leading-none">{cartTotals.total_qty}</span>
                  <span className="text-xs">items</span>
                </div>
              </div>
              <div className="ml-auto flex items-center gap-1.5 shrink-0">
                <span className="text-xs text-muted-foreground shrink-0">Overall Discount:</span>
                <input
                  ref={overallDiscountRef}
                  type="number"
                  min="0"
                  step="0.01"
                  value={overallDiscount}
                  onChange={e => setOverallDiscount(e.target.value)}
                  onKeyDown={e => {
                    blockNonNumeric(e);
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      e.preventDefault();
                      const n = parseFloat(overallDiscount) || 0;
                      const next = e.key === 'ArrowUp'
                        ? (Number.isInteger(n) ? n + 1 : Math.ceil(n))
                        : (Number.isInteger(n) ? Math.max(0, n - 1) : Math.floor(n));
                      setOverallDiscount(String(next));
                    }
                  }}
                  placeholder="0"
                  className="w-20 px-2 py-1 border border-border rounded-lg text-xs text-right focus:outline-none focus:ring-1 focus:ring-primary"
                  tabIndex={-1}
                />
                <button
                  tabIndex={-1}
                  onClick={() => { setOverallDiscountType(t => t === 'amount' ? 'percent' : 'amount'); refocusSearch(); }}
                  className="h-7 w-7 border border-border rounded-lg text-xs font-bold hover:bg-muted/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                  title={overallDiscountType === 'amount' ? 'Switch to percent' : 'Switch to rupees'}
                >
                  {overallDiscountType === 'amount' ? '₹' : '%'}
                </button>
                {(cartTotals.discount_amt > 0 || cartTotals.line_item_discount_amt > 0) && (
                  <span className="text-sm text-emerald-700 font-medium">− {fmt(cartTotals.line_item_discount_amt + cartTotals.discount_amt)}</span>
                )}
                {cartTotals.round_off !== 0 && (
                  <span className="text-sm text-slate-500">
                    Rnd {cartTotals.round_off > 0 ? '+' : ''}{fmt(cartTotals.round_off)}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Mobile-only: customer selector row */}
          {isMobile && (
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  ref={customerInputRef}
                  type="text"
                  value={customerSearch}
                  onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDrop(true); setCustomer(null); setKhataCollectAmount(''); setCustomerSelectedIdx(0); setRetailerGateError(null); }}
                  onFocus={() => { setShowCustomerDrop(true); if (customerSearch.trim()) setCustomerSelectedIdx(0); }}
                  onBlur={() => setTimeout(() => { setShowCustomerDrop(false); setCustomerSelectedIdx(-1); }, 150)}
                  onKeyDown={e => {
                    if (e.key === 'ArrowUp') { e.preventDefault(); if (!showCustomerDrop) { setShowCustomerDrop(true); setCustomerSelectedIdx(0); } else setCustomerSelectedIdx(i => Math.min(i + 1, filteredCustomers.length)); return; }
                    if (e.key === 'ArrowDown') { e.preventDefault(); if (!showCustomerDrop) { setShowCustomerDrop(true); setCustomerSelectedIdx(0); } else setCustomerSelectedIdx(i => Math.max(i - 1, 0)); return; }
                    if (e.key === 'Enter' && !e.ctrlKey) { e.preventDefault(); if (customerSelectedIdx >= 0 && customerSelectedIdx < filteredCustomers.length) { const c = filteredCustomers[customerSelectedIdx]; setCustomer(c); setCustomerSearch(c.name); setShowCustomerDrop(false); setCustomerSelectedIdx(-1); } else { const isPhone = /^\d+$/.test(customerSearch.trim()); if (isPhone) { setQuickMobile(customerSearch.trim()); setQuickName(''); } else { setQuickName(customerSearch.trim()); setQuickMobile(''); } setShowCustomerDrop(false); setCustomerSelectedIdx(-1); setQuickAddOpen(true); } return; }
                    if (e.key === 'Tab' && e.shiftKey) { e.preventDefault(); if (cart.length > 0) { upInputRefs.current[cart.length - 1]?.focus(); upInputRefs.current[cart.length - 1]?.select(); } else { searchRef.current?.focus(); } }
                  }}
                  placeholder="Select customer (optional)"
                  className="w-full pl-9 pr-4 py-2 rounded-lg border border-border text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                </span>
                {customer && (<button tabIndex={-1} onClick={() => { setCustomer(null); setCustomerSearch(''); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>)}
                {showCustomerDrop && customerSearch.trim().length > 0 && (
                  <div ref={customerDropdownRef} className="absolute bottom-full mb-1 left-0 right-0 bg-card border border-border rounded-lg shadow-lg z-40 max-h-48 overflow-y-auto">
                    <button onMouseDown={(e) => { e.preventDefault(); const isPhone = /^\d+$/.test(customerSearch.trim()); if (isPhone) { setQuickMobile(customerSearch.trim()); setQuickName(''); } else { setQuickName(customerSearch.trim()); setQuickMobile(''); } setShowCustomerDrop(false); setQuickAddOpen(true); }} className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground border-b border-border italic ${customerSelectedIdx === filteredCustomers.length ? 'bg-muted/60' : 'hover:bg-muted/60'}`}><UserPlus className="h-3.5 w-3.5 shrink-0" />Quick add &ldquo;{customerSearch.trim()}&rdquo;</button>
                    {[...filteredCustomers].reverse().map((c, reversedIdx) => { const actualIdx = filteredCustomers.length - 1 - reversedIdx; return (<button key={c.id} onMouseDown={(e) => { e.preventDefault(); setCustomer(c); setCustomerSearch(c.name); setShowCustomerDrop(false); setCustomerSelectedIdx(-1); setRetailerGateError(null); }} className={`w-full flex items-center justify-between px-4 py-2 text-sm text-left ${actualIdx === customerSelectedIdx ? 'bg-muted/60' : 'hover:bg-muted/60'}`}><span className="flex items-center gap-2 min-w-0"><span className="font-medium truncate">{c.name}</span>{c.kiranaflow_store_id && (<span className="shrink-0 inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-semibold leading-none">Connected</span>)}</span><span className="text-xs text-muted-foreground flex items-center gap-1.5 shrink-0 ml-2">{c.account_code != null && <span className="font-mono text-primary/70">#{c.account_code}</span>}{c.mobile}</span></button>); })}
                  </div>
                )}
              </div>
              <button tabIndex={-1} onClick={() => setQuickAddOpen(true)} className="p-3 rounded-lg border border-border hover:bg-muted/60 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" title="Quick add customer (Alt+C)"><UserPlus className="h-6 w-6" /></button>
            </div>
          )}

          {/* Supplier retailer-gate error panel */}
          {retailerGateError && customer && !customer.kiranaflow_store_id && (
            <div className="flex items-start gap-2.5 rounded-lg px-3 py-2.5 text-sm border bg-violet-50 border-violet-200 text-violet-800 dark:bg-violet-900/20 dark:border-violet-700 dark:text-violet-200">
              <span className="mt-0.5 shrink-0 text-base leading-none">🚀</span>
              <span className="flex-1 leading-snug"><strong>{customer.name}</strong> isn't connected to your Kiranaflow account yet. Ask your admin to link them — or if they haven't joined Kiranaflow yet, invite them to sign up!</span>
              <button onClick={() => setRetailerGateError(null)} className="shrink-0 opacity-60 hover:opacity-100 transition-opacity" aria-label="Dismiss"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}

          {/* Khata balance badge */}
          {customer && (customer.khata_balance ?? 0) > 0 && (
            <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
              <IndianRupee className="h-3.5 w-3.5 shrink-0" />
              <span className="font-medium">₹{(customer.khata_balance).toLocaleString('en-IN', { maximumFractionDigits: 0 })} outstanding khata</span>
              <span className="text-amber-500">{user?.role === 'supplier' ? '— balance due' : '— collect at payment'}</span>
            </div>
          )}

          {/* Totals + action buttons */}
          {isMobile ? (
            <div className="flex items-center justify-end gap-2">
              <button
                tabIndex={-1}
                onClick={() => { slotsRef.current[activeSlot] = { ...EMPTY_SLOT }; setCart([]); setCustomer(null); setCustomerSearch(''); setOverallDiscount(''); setOverallDiscountType('percent'); setKhataCollectAmount(''); }}
                className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted/60"
              >
                Clear Cart
              </button>
              <button
                ref={payBtnRef}
                tabIndex={0}
                onClick={() => user?.role === 'supplier' ? handleCheckout('khata') : setPaymentOpen(true)}
                onKeyDown={e => { if (e.key === 'Tab' && e.shiftKey) { e.preventDefault(); customerInputRef.current?.focus(); } }}
                className="px-6 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              >
                {user?.role === 'supplier' ? `Send Invoice ${fmt(cartTotals.rounded_total)}` : `Pay ${fmt(cartTotals.rounded_total)}`}
              </button>
            </div>
          ) : (
            /* Desktop: customer selector + clear + pay in one row */
            <div className="flex items-center gap-2">
              {/* Customer selector (flex-1) */}
              <div className="relative flex-1">
                <input
                  ref={customerInputRef}
                  type="text"
                  value={customerSearch}
                  onChange={e => { setCustomerSearch(e.target.value); setShowCustomerDrop(true); setCustomer(null); setKhataCollectAmount(''); setCustomerSelectedIdx(0); setRetailerGateError(null); }}
                  onFocus={() => { setShowCustomerDrop(true); if (customerSearch.trim()) setCustomerSelectedIdx(0); }}
                  onBlur={() => setTimeout(() => { setShowCustomerDrop(false); setCustomerSelectedIdx(-1); }, 150)}
                  onKeyDown={e => {
                    if (e.key === 'ArrowUp') { e.preventDefault(); if (!showCustomerDrop) { setShowCustomerDrop(true); setCustomerSelectedIdx(0); } else setCustomerSelectedIdx(i => Math.min(i + 1, filteredCustomers.length)); return; }
                    if (e.key === 'ArrowDown') { e.preventDefault(); if (!showCustomerDrop) { setShowCustomerDrop(true); setCustomerSelectedIdx(0); } else setCustomerSelectedIdx(i => Math.max(i - 1, 0)); return; }
                    if (e.key === 'Enter' && !e.ctrlKey) {
                      e.preventDefault();
                      if (customerSelectedIdx >= 0 && customerSelectedIdx < filteredCustomers.length) {
                        const c = filteredCustomers[customerSelectedIdx];
                        setCustomer(c); setCustomerSearch(c.name); setShowCustomerDrop(false); setCustomerSelectedIdx(-1);
                      } else {
                        const isPhone = /^\d+$/.test(customerSearch.trim());
                        if (isPhone) { setQuickMobile(customerSearch.trim()); setQuickName(''); }
                        else { setQuickName(customerSearch.trim()); setQuickMobile(''); }
                        setShowCustomerDrop(false); setCustomerSelectedIdx(-1); setQuickAddOpen(true);
                      }
                      return;
                    }
                    if (e.key === 'Tab' && e.shiftKey) {
                      e.preventDefault();
                      if (cart.length > 0) {
                        trashInputRefs.current[cart.length - 1]?.focus();
                      } else {
                        searchRef.current?.focus();
                      }
                    }
                  }}
                  placeholder="Select customer (optional)"
                  className="w-full pl-9 pr-4 py-1.5 rounded-lg border border-border text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                </span>
                {customer && (
                  <button tabIndex={-1} onClick={() => { setCustomer(null); setCustomerSearch(''); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
                {showCustomerDrop && customerSearch.trim().length > 0 && (
                  <div ref={customerDropdownRef} className="absolute bottom-full mb-1 left-0 right-0 bg-card border border-border rounded-lg shadow-lg z-40 max-h-48 overflow-y-auto">
                    <button
                      onMouseDown={(e) => { e.preventDefault(); const isPhone = /^\d+$/.test(customerSearch.trim()); if (isPhone) { setQuickMobile(customerSearch.trim()); setQuickName(''); } else { setQuickName(customerSearch.trim()); setQuickMobile(''); } setShowCustomerDrop(false); setQuickAddOpen(true); }}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground border-b border-border italic ${customerSelectedIdx === filteredCustomers.length ? 'bg-muted/60' : 'hover:bg-muted/60'}`}
                    >
                      <UserPlus className="h-3.5 w-3.5 shrink-0" />
                      Quick add &ldquo;{customerSearch.trim()}&rdquo;
                    </button>
                    {[...filteredCustomers].reverse().map((c, reversedIdx) => {
                      const actualIdx = filteredCustomers.length - 1 - reversedIdx;
                      return (
                        <button
                          key={c.id}
                          onMouseDown={(e) => { e.preventDefault(); setCustomer(c); setCustomerSearch(c.name); setShowCustomerDrop(false); setCustomerSelectedIdx(-1); setRetailerGateError(null); refocusSearch(); }}
                          className={`w-full flex items-center justify-between px-4 py-2 text-sm text-left ${actualIdx === customerSelectedIdx ? 'bg-muted/60' : 'hover:bg-muted/60'}`}
                        >
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="font-medium truncate">{c.name}</span>
                            {c.kiranaflow_store_id && (<span className="shrink-0 inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-semibold leading-none">Connected</span>)}
                          </span>
                          <span className="text-xs text-muted-foreground flex items-center gap-1.5 shrink-0 ml-2">
                            {c.account_code != null && <span className="font-mono text-primary/70">#{c.account_code}</span>}
                            {c.mobile}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {/* Quick-add customer button */}
              <button
                tabIndex={-1}
                onClick={() => setQuickAddOpen(true)}
                className="p-2 rounded-lg border border-border hover:bg-muted/60 text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary shrink-0"
                title="Quick add customer (Alt+C)"
              >
                <UserPlus className="h-4 w-4" />
              </button>
              {/* Clear Cart */}
              <button
                tabIndex={-1}
                onClick={() => { slotsRef.current[activeSlot] = { ...EMPTY_SLOT }; setCart([]); setCustomer(null); setCustomerSearch(''); setOverallDiscount(''); setOverallDiscountType('percent'); setKhataCollectAmount(''); refocusSearch(); }}
                className="px-4 py-1.5 rounded-lg border border-border text-sm font-medium hover:bg-muted/60 shrink-0"
              >
                Clear Cart
              </button>
              {/* Pay */}
              <button
                ref={payBtnRef}
                tabIndex={0}
                onClick={() => user?.role === 'supplier' ? handleCheckout('khata') : setPaymentOpen(true)}
                onKeyDown={e => { if (e.key === 'Tab' && e.shiftKey) { e.preventDefault(); customerInputRef.current?.focus(); } }}
                className="px-6 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-emerald-400 shrink-0"
              >
                {user?.role === 'supplier' ? `Send Invoice ${fmt(cartTotals.rounded_total)}` : `Pay ${fmt(cartTotals.rounded_total)}`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Keyboard shortcuts modal ── */}
      {shortcutsOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShortcutsOpen(false)}>
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-sm max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-border sticky top-0 bg-card">
              <h2 className="text-sm font-semibold flex items-center gap-2"><Keyboard className="h-4 w-4" />Keyboard Shortcuts</h2>
              <button onClick={() => setShortcutsOpen(false)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
            </div>

            <div className="px-4 pt-3 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">POS</div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 px-4 py-3 text-xs">
              {[
                ['Tab / Shift+Tab', 'Navigate Qty → MRP → Discount → Unit Cost through cart'],
                ['Ctrl+Enter / F9', 'Checkout'],
                ['Alt+Q',          'Edit quantity (last item)'],
                ['Alt+D',          'Edit discount (last item)'],
                ['Alt+U',          'Edit unit cost (last item)'],
                ['Alt+M',          'Change MRP + save to stock master'],
                ['Ctrl+Shift+M',   'Open item in stock master'],
                ['Alt+P',          'Quick add product'],
                ['Alt+C',          'Quick add customer'],
                ['Ctrl+1/2/3',     'Switch customer slot'],
                ['Ctrl+←/→',       'Previous / next slot'],
                ['/',              'Focus search'],
                ['Esc',            'Close dropdowns / discard cart field edit'],
              ].map(([key, label]) => (
                <React.Fragment key={key}>
                  <dt><kbd className="font-mono bg-muted px-1.5 py-0.5 rounded border border-border text-[10px] whitespace-nowrap">{key}</kbd></dt>
                  <dd className="text-muted-foreground self-center">{label}</dd>
                </React.Fragment>
              ))}
            </dl>

            <div className="px-4 pt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground border-t border-border">Navigation</div>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 px-4 py-3 text-xs">
              {[
                ['Ctrl+B',  'Go to POS'],
                ['Ctrl+M',  'Go to Appendix'],
                ['Alt+↑/↓', 'Previous / next sidebar page'],
              ].map(([key, label]) => (
                <React.Fragment key={key}>
                  <dt><kbd className="font-mono bg-muted px-1.5 py-0.5 rounded border border-border text-[10px] whitespace-nowrap">{key}</kbd></dt>
                  <dd className="text-muted-foreground self-center">{label}</dd>
                </React.Fragment>
              ))}
            </dl>

          </div>
        </div>
      )}

      {/* ── Payment modal ─────────────────────────────────────────────────────── */}
      {paymentOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border">
              <h2 className="text-lg font-bold">Select Payment Mode</h2>
              <button onClick={() => { setPaymentOpen(false); setKhataCollectAmount(''); }} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-5">
              {!splitMode ? (
                <>
                  <p className="text-xs text-muted-foreground mb-4">← → ↑ ↓ or 1-4 choose mode · S split · Enter confirm · Esc cancel</p>
                  <div className="grid grid-cols-2 gap-3 mb-5">
                    {PAYMENT_MODES.map((m, i) => {
                      const disabled  = m.key === 'khata' && !customer;
                      const isSelected = selectedMode === m.key;
                      return (
                        <button
                          key={m.key}
                          onClick={() => { if (!disabled) setSelectedMode(m.key); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !disabled) { e.preventDefault(); confirmBtnRef.current?.click(); } }}
                          disabled={disabled}
                          className={`relative py-6 rounded-2xl font-semibold text-base flex flex-col items-center justify-center gap-2 transition-all select-none
                            ${isSelected
                              ? m.color + ' shadow-md ring-2 ring-offset-2 ring-current/40 scale-[1.02]'
                              : 'bg-card border border-border text-foreground hover:bg-muted/50'}
                            ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span className="absolute top-2 left-3 text-[11px] font-normal opacity-40">{i + 1}</span>
                          <m.Icon className="h-6 w-6" />
                          <span className="text-sm font-semibold">{m.label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setSplitMode(true)}
                    className="text-sm text-primary underline underline-offset-2 w-full text-center mb-4"
                  >
                    Split between multiple payment methods
                  </button>
                </>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground mb-3">
                    Enter the amount for each payment method. Total must equal {fmt(cartTotals.rounded_total)}.
                  </p>
                  <div className="space-y-3 mb-4">
                    {PAYMENT_MODES.map(m => {
                      const disabled = m.key === 'khata' && !customer;
                      return (
                        <div key={m.key} className={`flex items-center gap-3 ${disabled ? 'opacity-40' : ''}`}>
                          <span className="w-32 text-sm font-medium flex items-center gap-2 text-gray-700 shrink-0">
                            <m.Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                            {m.label}
                          </span>
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm pointer-events-none">₹</span>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={splitAmounts[m.key]}
                              onChange={e => setSplitAmounts(prev => ({ ...prev, [m.key]: e.target.value }))}
                              disabled={disabled}
                              placeholder="0"
                              className="w-full pl-7 pr-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          </div>
                        </div>
                      );
                    })}
                    <div className={`flex justify-between items-center text-sm font-semibold px-3 py-2.5 rounded-lg mt-1 ${
                      splitRemaining === 0  ? 'text-emerald-700 bg-emerald-50' :
                      splitRemaining > 0    ? 'text-amber-600 bg-amber-50'    : 'text-red-600 bg-red-50'
                    }`}>
                      <span>Remaining</span>
                      <span>{fmt(Math.abs(splitRemaining))}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => { setSplitMode(false); setSplitAmounts({ cash: '', card: '', upi: '', khata: '' }); }}
                    className="text-sm text-primary underline underline-offset-2 w-full text-center mb-4"
                  >
                    Back to single payment
                  </button>
                </>
              )}

              {/* Also collect outstanding khata — hidden for supplier (collection only via Credit Customers page) */}
              {user?.role !== 'supplier' && customer && (customer.khata_balance ?? 0) > 0 && selectedMode !== 'khata' && !splitMode && isOnline && (
                <div className="mb-3 px-3 py-3 bg-amber-50 border border-amber-200 rounded-xl space-y-2">
                  <p className="text-xs font-medium text-amber-800 flex items-center gap-1.5">
                    <IndianRupee className="h-3.5 w-3.5" />
                    {customer.name} has ₹{(customer.khata_balance).toLocaleString('en-IN', { maximumFractionDigits: 0 })} outstanding
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-amber-700 shrink-0">Also collect:</span>
                    <div className="relative flex-1">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-amber-600 text-xs font-medium pointer-events-none">₹</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        max={customer.khata_balance}
                        value={khataCollectAmount}
                        onChange={e => setKhataCollectAmount(e.target.value)}
                        placeholder="0  (optional)"
                        className="w-full pl-6 pr-3 py-1.5 border border-amber-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-amber-400 bg-background"
                      />
                    </div>
                    <button
                      onClick={() => setKhataCollectAmount(String(customer.khata_balance))}
                      className="text-xs text-amber-700 underline underline-offset-2 whitespace-nowrap"
                    >
                      Full
                    </button>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2 border-t border-border">
                <button
                  onClick={() => { setPaymentOpen(false); setKhataCollectAmount(''); }}
                  className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted/60"
                >
                  Cancel
                </button>
                <button
                  ref={confirmBtnRef}
                  onClick={() => handleCheckout()}
                  disabled={checkoutLoading || (splitMode && Math.abs(splitRemaining) > 0.01)}
                  className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 transition-colors whitespace-normal leading-tight text-center min-h-[2.75rem]"
                >
                  {checkoutLoading ? 'Processing…' : <><span className="block">Confirm Payment</span><span className="block">{fmt(cartTotals.rounded_total)}</span></>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Quick add customer modal ──────────────────────────────────────────── */}
      {quickAddOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border">
              <h2 className="text-base font-bold flex items-center gap-2">
                <UserPlus className="h-4 w-4" /> Quick Add Customer
              </h2>
              <button onClick={() => setQuickAddOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Customer Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={quickName}
                  onChange={e => setQuickName(e.target.value)}
                  placeholder="e.g. Ramesh Shah"
                  autoFocus
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Mobile Number</label>
                <input
                  type="tel"
                  value={quickMobile}
                  onChange={e => setQuickMobile(e.target.value)}
                  placeholder="9876543210"
                  onKeyDown={e => e.key === 'Enter' && handleQuickAdd()}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <p className="text-xs text-muted-foreground">Customer will be added to the database and auto-selected in the cart.</p>
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setQuickAddOpen(false)}
                  className="flex-1 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted/60"
                >
                  Cancel
                </button>
                <button
                  onClick={handleQuickAdd}
                  disabled={quickLoading || !quickName.trim()}
                  className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <UserPlus className="h-4 w-4" />
                  {quickLoading ? 'Adding…' : 'Add & Select'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Quick add product modal ──────────────────────────────────────────── */}
      {qaProductOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-sm overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-yellow-500 px-6 pt-5 pb-4 flex items-start justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-amber-100 mb-0.5">New product</p>
                <h2 className="text-lg font-bold text-white leading-tight flex items-center gap-2">
                  <Package className="h-4 w-4" /> Quick Add Product
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wide text-amber-900 bg-amber-200 px-1.5 py-0.5 rounded">+ New item</span>
                <button onClick={closeQaModal} className="text-white/70 hover:text-white">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Product Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={qaProductForm.name}
                  onChange={e => setQaProductForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Amul Butter 100g"
                  autoFocus
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Barcode <span className="text-muted-foreground text-xs font-normal">(optional)</span></label>
                <input
                  type="text"
                  value={qaProductBarcode}
                  onChange={e => setQaProductBarcode(e.target.value)}
                  placeholder="Scan or enter barcode"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
                <button
                  type="button"
                  disabled={qaSuggestingBarcode}
                  onClick={async () => {
                    setQaSuggestingBarcode(true);
                    const isReset = qaSuggestCount >= 10;
                    const after = (!isReset && qaSuggestCount > 0 && qaProductBarcode)
                      ? parseInt(qaProductBarcode, 10) : undefined;
                    try {
                      const res = await purchasesAPI.suggestBarcode(after);
                      setQaProductBarcode(res.data.barcode);
                      setQaSuggestCount(isReset ? 1 : qaSuggestCount + 1);
                    } catch { /* silently ignore */ } finally {
                      setQaSuggestingBarcode(false);
                    }
                  }}
                  className="mt-0.5 text-[10px] text-primary underline underline-offset-2 hover:opacity-70 disabled:opacity-40 transition-opacity"
                >
                  {qaSuggestingBarcode ? 'Suggesting…' : qaSuggestCount === 0 ? 'Suggest code' : 'Suggest next available'}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">MRP <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={qaProductForm.mrp}
                    onChange={e => setQaProductForm(f => ({ ...f, mrp: e.target.value }))}
                    onKeyDown={e => { blockNonNumeric(e); if (e.key === 'Enter') handleQuickAddProduct(); }}
                    placeholder="0.00"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-medium">Discount</label>
                    <div className="flex rounded-md border border-border overflow-hidden text-xs font-semibold">
                      <button
                        type="button"
                        onClick={() => {
                          if (qaDiscountMode === 'percent') return;
                          setQaDiscountMode('percent');
                          setQaDiscountRs('');
                        }}
                        className={`px-2 py-0.5 transition-colors ${qaDiscountMode === 'percent' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/60'}`}
                      >%</button>
                      <button
                        type="button"
                        onClick={() => {
                          if (qaDiscountMode === 'rupees') return;
                          const mrpVal = parseFloat(qaProductForm.mrp);
                          const pctVal = parseFloat(qaProductForm.discount);
                          const prefilledRs = (mrpVal > 0 && pctVal > 0)
                            ? String(Math.round(mrpVal * pctVal / 100 * 100) / 100)
                            : '';
                          setQaDiscountRs(prefilledRs);
                          setQaDiscountMode('rupees');
                        }}
                        className={`px-2 py-0.5 transition-colors ${qaDiscountMode === 'rupees' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted/60'}`}
                      >₹</button>
                    </div>
                  </div>
                  <input
                    type="number"
                    min="0"
                    max={qaDiscountMode === 'percent' ? 100 : undefined}
                    step="0.01"
                    value={qaDiscountMode === 'percent' ? qaProductForm.discount : qaDiscountRs}
                    onChange={e => {
                      if (qaDiscountMode === 'percent') {
                        setQaProductForm(f => ({ ...f, discount: e.target.value }));
                      } else {
                        setQaDiscountRs(e.target.value);
                        const mrpVal = parseFloat(qaProductForm.mrp);
                        const rsVal = parseFloat(e.target.value);
                        const pct = (mrpVal > 0 && rsVal >= 0)
                          ? String(Math.round(rsVal / mrpVal * 10000) / 100)
                          : '';
                        setQaProductForm(f => ({ ...f, discount: pct }));
                      }
                    }}
                    onKeyDown={e => { blockNonNumeric(e); if (e.key === 'Enter') handleQuickAddProduct(); }}
                    placeholder="0 (optional)"
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                  />
                  {(() => {
                    const mrpVal = parseFloat(qaProductForm.mrp);
                    if (!mrpVal || mrpVal <= 0) return null;
                    if (qaDiscountMode === 'rupees') {
                      const rsVal = parseFloat(qaDiscountRs);
                      if (!rsVal || rsVal <= 0) return null;
                      const pct = Math.round(rsVal / mrpVal * 10000) / 100;
                      return <p className="text-xs text-muted-foreground mt-1">= {pct}%</p>;
                    } else {
                      const pctVal = parseFloat(qaProductForm.discount);
                      if (!pctVal || pctVal <= 0) return null;
                      const rs = Math.round(mrpVal * pctVal / 100 * 100) / 100;
                      return <p className="text-xs text-muted-foreground mt-1">= ₹{rs}</p>;
                    }
                  })()}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Cost Price</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={qaProductForm.cost_price}
                  onChange={e => setQaProductForm(f => ({ ...f, cost_price: e.target.value }))}
                  onKeyDown={blockNonNumeric}
                  placeholder="0 (enter later)"
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Qty being purchased</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={qaProductForm.qty}
                  onChange={e => setQaProductForm(f => ({ ...f, qty: e.target.value }))}
                  onKeyDown={e => { blockNonNumeric(e); if (e.key === 'Enter') handleQuickAddProduct(); }}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>
              {!isOnline && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  Offline — product will be registered and stock updated when you reconnect.
                </p>
              )}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={closeQaModal}
                  className="flex-1 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted/60"
                >
                  Cancel
                </button>
                <button
                  onClick={handleQuickAddProduct}
                  disabled={qaProductLoading || !qaProductForm.name.trim() || !qaProductForm.mrp}
                  className="flex-1 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Package className="h-4 w-4" />
                  {qaProductLoading ? 'Adding…' : 'Add & Sell'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Order import modal ───────────────────────────────────────────────── */}
      {orderImportOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={e => { if (e.target === e.currentTarget) closeOrderImport(); }}>
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">

            {/* Header */}
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-6 pt-5 pb-4 flex items-start justify-between shrink-0">
              <div>
                <h2 className="text-white font-semibold text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {orderImportStep === 1 ? 'Import Order List' : 'Review & Add to Cart'}
                </h2>
                <p className="text-violet-200 text-xs mt-0.5">
                  {orderImportStep === 1
                    ? 'Upload a photo, PDF, or paste a WhatsApp message'
                    : `${matchedOrder.length} item${matchedOrder.length !== 1 ? 's' : ''} found — review matches before adding`}
                </p>
              </div>
              <button onClick={closeOrderImport} className="text-white/70 hover:text-white mt-0.5">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* ── Step 1: Upload ── */}
            {orderImportStep === 1 && (
              <div className="p-5 flex flex-col gap-4 overflow-y-auto">

                {/* Mode tabs */}
                <div className="flex gap-1 bg-muted rounded-lg p-1 text-sm">
                  <button
                    onClick={() => setOrderImportMode('file')}
                    className={`flex-1 py-1.5 rounded-md font-medium transition-colors ${orderImportMode === 'file' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
                  >
                    Upload File / Photo
                  </button>
                  <button
                    onClick={() => setOrderImportMode('text')}
                    className={`flex-1 py-1.5 rounded-md font-medium transition-colors ${orderImportMode === 'text' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
                  >
                    Paste Text
                  </button>
                </div>

                {orderImportMode === 'file' ? (
                  <div
                    onDragOver={e => { e.preventDefault(); setOrderImportDragging(true); }}
                    onDragLeave={() => setOrderImportDragging(false)}
                    onDrop={e => {
                      e.preventDefault();
                      setOrderImportDragging(false);
                      const f = e.dataTransfer.files[0];
                      if (f) setOrderImportFile(f);
                    }}
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                      orderImportDragging ? 'border-violet-400 bg-violet-50' : 'border-border hover:border-violet-300 hover:bg-violet-50/40'
                    }`}
                    onClick={() => document.getElementById('order-import-file-input').click()}
                  >
                    <input
                      id="order-import-file-input"
                      type="file"
                      className="hidden"
                      accept="image/*,.pdf"
                      onChange={e => setOrderImportFile(e.target.files[0] || null)}
                    />
                    {orderImportFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <CheckCircle className="h-10 w-10 text-violet-500" />
                        <span className="font-medium text-sm text-foreground">{orderImportFile.name}</span>
                        <span className="text-xs text-muted-foreground">{(orderImportFile.size / 1024).toFixed(0)} KB</span>
                        <button
                          onClick={e => { e.stopPropagation(); setOrderImportFile(null); }}
                          className="text-xs text-red-500 hover:text-red-700 underline"
                        >Remove</button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Upload className="h-10 w-10 opacity-40" />
                        <span className="font-medium text-sm">Drop file here or click to browse</span>
                        <span className="text-xs">Supports: photos of handwritten notes, WhatsApp screenshots, PDFs</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <label className="text-sm font-medium text-foreground">Paste your order text</label>
                    <textarea
                      className="w-full h-40 px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-400/40 focus:border-violet-400 resize-none"
                      placeholder={"Examples:\n• 2 Surf Excel 1kg, 3 Amul Butter, 5 Parle G\n• do kilo doodh, ek packet bread\n• Colgate 200g x4, Vim Bar x3"}
                      value={orderImportText}
                      onChange={e => setOrderImportText(e.target.value)}
                      autoFocus
                    />
                  </div>
                )}

                <div className="flex gap-3 pt-1">
                  <button onClick={closeOrderImport} className="flex-1 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted/60">
                    Cancel
                  </button>
                  <button
                    onClick={handleParseOrder}
                    disabled={orderImportLoading || (orderImportMode === 'file' ? !orderImportFile : !orderImportText.trim())}
                    className="flex-1 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {orderImportLoading ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                        </svg>
                        Parsing…
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4" />
                        Parse Document
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* ── Step 2: Review ── */}
            {orderImportStep === 2 && (
              <div className="flex flex-col overflow-hidden">
                {/* Overall confidence banner */}
                {parsedOrder && (
                  <div className={`px-5 py-2 text-xs font-medium flex items-center gap-2 shrink-0 ${
                    parsedOrder.confidence === 'high'   ? 'bg-emerald-50 text-emerald-700 border-b border-emerald-100' :
                    parsedOrder.confidence === 'medium' ? 'bg-amber-50  text-amber-700  border-b border-amber-100'   :
                                                          'bg-red-50    text-red-700    border-b border-red-100'
                  }`}>
                    {parsedOrder.confidence === 'high'   && <CheckCircle  className="h-3.5 w-3.5 shrink-0" />}
                    {parsedOrder.confidence === 'medium' && <AlertCircle  className="h-3.5 w-3.5 shrink-0" />}
                    {parsedOrder.confidence === 'low'    && <HelpCircle   className="h-3.5 w-3.5 shrink-0" />}
                    Overall parse confidence: <span className="uppercase font-bold">{parsedOrder.confidence}</span>
                    &nbsp;— verify low-confidence matches before proceeding
                  </div>
                )}

                {/* Items table */}
                <div className="overflow-y-auto flex-1 px-5 py-3">
                  {matchedOrder.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      No items could be extracted from the document.
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {matchedOrder.map((row, idx) => (
                        <div
                          key={idx}
                          className={`rounded-lg border px-3 py-2.5 flex items-start gap-3 transition-colors ${
                            row.selected ? 'border-violet-200 bg-violet-50/40' : 'border-border bg-muted/20 opacity-60'
                          }`}
                        >
                          {/* Checkbox */}
                          <input
                            type="checkbox"
                            className="mt-1 accent-violet-600 shrink-0"
                            checked={!!row.selected}
                            disabled={!row.match}
                            onChange={e => setMatchedOrder(prev => prev.map((r, i) => i === idx ? { ...r, selected: e.target.checked } : r))}
                          />

                          {/* Names */}
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-muted-foreground truncate">From doc: &ldquo;{row.parsed_name}&rdquo;</div>
                            {row.match ? (
                              <div className="font-medium text-sm text-foreground truncate mt-0.5">{row.match.name || row.match.product_name}</div>
                            ) : (
                              <div className="text-sm text-red-500 mt-0.5 italic">No match found in catalog</div>
                            )}
                          </div>

                          {/* Confidence badge */}
                          <span className={`shrink-0 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded mt-0.5 ${
                            row.confidence === 'high'      ? 'bg-emerald-100 text-emerald-700' :
                            row.confidence === 'medium'    ? 'bg-amber-100  text-amber-700'   :
                            row.confidence === 'low'       ? 'bg-orange-100 text-orange-700'  :
                                                             'bg-red-100    text-red-600'
                          }`}>
                            {row.confidence === 'not_found' ? '—' : row.confidence}
                          </span>

                          {/* Qty */}
                          <div className="flex items-center gap-1 shrink-0">
                            <span className="text-xs text-muted-foreground">Qty</span>
                            <input
                              type="number"
                              min="0.5"
                              step="1"
                              value={row.qty}
                              onChange={e => {
                                const v = parseFloat(e.target.value) || 1;
                                setMatchedOrder(prev => prev.map((r, i) => i === idx ? { ...r, qty: v } : r));
                              }}
                              className="w-14 text-center px-1 py-0.5 border border-border rounded text-sm focus:outline-none focus:ring-1 focus:ring-violet-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer actions */}
                <div className="px-5 py-4 border-t border-border flex gap-3 shrink-0">
                  <button onClick={() => setOrderImportStep(1)} className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted/60">
                    ← Back
                  </button>
                  <button
                    onClick={() => setMatchedOrder(prev => prev.map(r => ({ ...r, selected: !!r.match })))}
                    className="px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:bg-muted/60"
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleAddImportedToCart}
                    disabled={!matchedOrder.some(r => r.selected && r.match)}
                    className="flex-1 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <ShoppingCart className="h-4 w-4" />
                    Add {matchedOrder.filter(r => r.selected && r.match).length} item{matchedOrder.filter(r => r.selected && r.match).length !== 1 ? 's' : ''} to Cart
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Receipt modal ─────────────────────────────────────────────────────── */}
      {receiptSale && (
        <ReceiptModal
          sale={receiptSale}
          storeProfile={storeProfile}
          onClose={() => setReceiptSale(null)}
        />
      )}
    </div>
  );
}
